
        // hardware tier is computed once in theme.liquid (runs on every page, pre-paint)
        const INNO_PERF = window.INNO_PERF || { tier: 'high', spacing: 1.2, pixelCap: 2, antialias: true };
        // reduced-motion: keep the field alive but slow it to a calm drift (see animate loop)
        const INNO_MOTION_CALM = document.documentElement.classList.contains('motion-reduce');

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(45, window.innerWidth / window.innerHeight, 0.1, 1000);
        const renderer = new THREE.WebGLRenderer({ antialias: INNO_PERF.antialias, alpha: true });
        const fieldBox = document.getElementById('webgl-container');
        // `false`: don't let three.js write inline width/height onto the canvas — the stylesheet
        // stretches it to the container, which is the thing that actually spans the screen
        renderer.setSize(fieldBox.clientWidth || window.innerWidth, fieldBox.clientHeight || window.innerHeight, false);
        renderer.setPixelRatio(Math.min(window.devicePixelRatio, INNO_PERF.pixelCap));
        document.getElementById('webgl-container').appendChild(renderer.domElement);

        const CAM_Z = 100;
        camera.position.z = CAM_Z;

        /* How far a lean swings the camera AROUND the field. The gyro used to slide the camera
           sideways and re-aim it at the centre, which moves the picture but never changes the
           angle you see it from — so a lean read as a nudge. On an arc of the same radius the
           angle does change: lean right and you come round to the field's right side, where the
           waves stop being a flat sheet and take on a near edge and a far one.

           These are taken as far as the geometry allows rather than set by taste, because the
           only thing that limits the swing is the moment the overscanned grid's far edge comes
           into frame. A point at distance X across the plane sits inside the frustum while
                X · (cosθ − tan(½fov) · sinθ) ≤ (half the visible span)
           and the grid runs OVERSCAN times the visible span, so the largest usable angle falls
           out of  cosθ − tan(½fov)·sinθ ≥ 1 / OVERSCAN  — no camera distance in it, only the
           field of view and how far the grid is built past the frame.

           That limit moves with the shape of the screen: a phone held upright has a narrow
           horizontal field and can swing much further than the same phone turned sideways. So it
           is solved per resize rather than pinned to one number.

           ONE AXIS, NOT TWO. Pitch used to be its own axis, read from the phone's forward tilt,
           and it was the unstable half: forward tilt has no dependable resting value — it changes
           with how you happen to be holding the thing, it wanders while you use the screen, and
           once its zero was set at one posture the camera simply sat permanently high and stayed
           there, sliding the whole field north and taking the touch response with it. Roll has a
           rest position you can feel (the screen level in your hands) and returns to it, so roll
           is the only thing the camera listens to now.

           Height comes from the SAME roll, not a second sensor: swinging east lifts the camera,
           swinging west drops it, so the move is one diagonal sweep around the field rather than
           two independent nudges. It costs nothing in stability — at rest the input is zero and
           the camera is exactly where it started — and it reads as a much bigger move than the
           two axes did separately. Leaning also pulls the camera in, which is what leaning in to
           look at something actually does, and buys back several degrees of swing on the same
           grid. */
        let ORBIT_YAW = 0.36;                       // east-west, recomputed on every resize
        const ORBIT_RISE = 0.5;                     // height gained per unit of swing
        const ORBIT_MARGIN = 0.70;                  // stay this side of the edge appearing
        const ORBIT_PULL = 16;                      // how far the camera comes in at a full lean
        function orbitLimits() {
            const tanH = Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * camera.aspect;
            const near = (CAM_Z - ORBIT_PULL) / CAM_Z;   // the lean-in relaxes the constraint
            let best = 0;
            for (let deg = 2; deg <= 80; deg++) {
                const t = THREE.MathUtils.degToRad(deg);
                if (Math.cos(t) - tanH * Math.sin(t) < near / OVERSCAN) break;
                best = t;
            }
            /* The margin is doing two jobs: it holds back from the edge, and it pays for the
               height, which this horizontal solve does not know about. Swept against the real
               camera it lands around 80% of the angle that would actually show an edge. */
            ORBIT_YAW = best * ORBIT_MARGIN;
        }

        // covers the deepest hover pull-back (z 145) plus lateral drift and
        // look-target swings — the grid must never show an edge
        const OVERSCAN = 2.6;   // covers the deepest hover pull-back (cart) + shift, so
                                // corner dots always exist under a hovered item
        const SPACING = INNO_PERF.spacing;   // denser on strong GPUs, sparser on weak ones

        const material = new THREE.ShaderMaterial({
            uniforms: {
                uTime: { value: 0 },
                uMode: { value: 1.0 },        // field: 1 wave, 2 pulse, 3 halftone, 4 noise
                uModeB: { value: 1.0 },       // crossfade target field
                uModeMix: { value: 0.0 },     // 0 = uMode, 1 = uModeB
                uPointerMode: { value: 1.0 }, // cursor: 0 off .. 12 twist
                uColor: { value: new THREE.Color(0xE8E4DC) },
                uMouse: { value: new THREE.Vector2(9999, 9999) },
                uMouseVel: { value: new THREE.Vector2(0, 0) },
                uMouseStrength: { value: 0 },
                uRadius: { value: 30.0 },
                uPower: { value: 1.0 },
                uClickPos: { value: [0, 1, 2, 3, 4].map(() => new THREE.Vector2(9999, 9999)) },
                uClickTime: { value: [-100, -100, -100, -100, -100] },
                uBoot: { value: 0 },
                uAssemble: { value: 0 },
                uWaveAmt: { value: 0.55 },   // wave-brightness preset amount
                uInvert: { value: 0 },      // 0 = void ground / bone dots, 1 = inverted
                uInvertMode: { value: 2 },  // 0 fade, 1 sweep, 2 ripple-from-click
                uInvertCenter: { value: new THREE.Vector2(0, 0) },
                uHint: { value: 0 },        // nav-hover inversion preview strength
                uHintCenter: { value: new THREE.Vector2(0, 0) },   // item center in screen NDC
                uHintSize: { value: new THREE.Vector2(0.1, 0.03) },  // item half-extents in NDC
                uAccent: { value: new THREE.Color(0x6E1420) },
                uCardMix: { value: 0 },     // 0 = field, 1 = dots composed into a page layout
                uDest: { value: 0 },        // which layout: 0 = shop cards, 1 = cart page
                uTransMode: { value: 0 }    // transition: 0 cascade, 1 dissolve, 2 magnetic
            },
            vertexShader: `
                uniform float uTime;
                uniform float uMode;
                uniform float uModeB;
                uniform float uModeMix;
                uniform float uPointerMode;
                uniform vec2 uMouse;
                uniform vec2 uMouseVel;
                uniform float uMouseStrength;
                uniform float uRadius;
                uniform float uPower;
                uniform vec2 uClickPos[5];
                uniform float uClickTime[5];
                uniform float uBoot;
                uniform float uAssemble;
                uniform float uWaveAmt;
                uniform float uCardMix;
                uniform float uDest;
                uniform float uTransMode;
                uniform float uInvert;
                uniform float uInvertMode;
                uniform vec2 uInvertCenter;
                uniform float uHint;
                uniform vec2 uHintCenter;
                uniform vec2 uHintSize;
                attribute vec2 aCard;
                attribute vec2 aBag;
                attribute vec2 aArc;
                attribute vec2 aAbt;
                varying float vOpacity;
                varying vec2 vUv;
                varying float vHalf;
                varying float vInv;
                varying float vHint;
                varying float vGlow;
                varying float vFoam;

                float fieldZ(float m, vec2 xy, float t) {
                    if (m == 1.0) return sin(xy.x * 0.1 + t) * 8.0 + cos(xy.y * 0.1 + t) * 4.0;   // wave
                    if (m == 2.0) return sin(length(xy) * 0.15 - t * 2.0) * 10.0;                 // pulse
                    if (m == 4.0) return sin(xy.x * 10.0) * cos(xy.y * 10.0 + t) * 5.0;           // noise
                    // ripple — two point sources interfering
                    if (m == 5.0) return sin(length(xy - vec2(-95.0, 0.0)) * 0.12 - t * 2.0) * 5.0
                                       + sin(length(xy - vec2( 95.0, 0.0)) * 0.12 - t * 2.0) * 5.0;
                    // swirl — a rotating spiral
                    if (m == 6.0) return sin(atan(xy.y, xy.x) * 3.0 + length(xy) * 0.07 - t * 2.0) * 8.0;
                    // ridges — long curved dunes drifting sideways
                    if (m == 7.0) return sin(xy.x * 0.13 + sin(xy.y * 0.05 + t) * 2.6 - t * 1.1) * 9.0;
                    // weave — a diagonal cross-hatch swell
                    if (m == 8.0) return (sin(xy.x * 0.17 + t) + sin(xy.y * 0.17 - t * 1.3)) * 5.0;
                    // whirlpool — a spiral drawing inward, stronger toward the centre
                    if (m == 9.0) return sin(atan(xy.y, xy.x) * 2.0 + length(xy) * 0.11 + t * 2.2)
                                       * (9.0 - length(xy) * 0.018);
                    // dome — a breathing central bulge (rises and falls)
                    if (m == 10.0) return cos(length(xy) * 0.055) * 12.0 * (0.45 + 0.55 * sin(t * 0.9));
                    // chevron — V-shaped waves marching upward
                    if (m == 11.0) return sin(abs(xy.x) * 0.09 + xy.y * 0.05 - t * 1.6) * 9.0;
                    return 0.0;   // halftone (3): flat plane
                }

                void main() {
                    vUv = uv;
                    vec3 pos = position;
                    float t = uTime * 0.8;

                    // ---- ambient field (A→B crossfade for menu-hover changes) ----
                    float fz = mix(fieldZ(uMode, position.xy, t), fieldZ(uModeB, position.xy, t), uModeMix);
                    pos.z = fz;
                    vHalf = mix(uMode == 3.0 ? 1.0 : 0.0, uModeB == 3.0 ? 1.0 : 0.0, uModeMix);

                    // ---- wave-shape analysis: crest height + whether it's breaking ----
                    float hsh = fract(sin(dot(position.xy, vec2(12.9898, 78.233))) * 43758.5453);
                    float wave = smoothstep(-12.0, 12.0, fz);
                    float band = 0.5 + 0.5 * sin(position.x * 0.09 + position.y * 0.055 - uTime * 1.5);
                    float crest = clamp(wave * 0.72 + band * 0.28, 0.0, 1.0);
                    float tPrev = t - 0.13;
                    float fzPrev = mix(fieldZ(uMode, position.xy, tPrev), fieldZ(uModeB, position.xy, tPrev), uModeMix);
                    float descending = clamp((fzPrev - fz) * 0.6, 0.0, 1.0);

                    // ---- intro: every dot flies in from a scattered start and
                    // assembles into the grid, then hands off to the live field ----
                    float asmFade = 1.0;
                    if (uAssemble < 0.999) {
                        float h1 = fract(sin(dot(position.xy, vec2(12.9898, 78.233))) * 43758.5453);
                        float h2 = fract(sin(dot(position.xy, vec2(39.346, 11.135))) * 24634.6345);
                        float h3 = fract(sin(dot(position.xy, vec2(73.156, 52.235))) * 51928.3021);
                        // per-dot arrival stagger, then a soft ease into place
                        float local = clamp((uAssemble - h1 * 0.45) / 0.55, 0.0, 1.0);
                        float e = local * local * local * (local * (local * 6.0 - 15.0) + 10.0);
                        vec3 scatter = vec3((h1 - 0.5) * 780.0, (h2 - 0.5) * 600.0, (h3 - 0.5) * 520.0 - 130.0);
                        pos.x = mix(scatter.x, position.x, e);
                        pos.y = mix(scatter.y, position.y, e);
                        pos.z = mix(scatter.z, fz, e);
                        asmFade = e;
                    }

                    // ---- cursor interaction ----
                    float mDist = distance(pos.xy, uMouse);
                    float influence = smoothstep(uRadius, 0.0, mDist) * uMouseStrength * uPower;
                    vec2 dir = mDist > 0.001 ? (pos.xy - uMouse) / mDist : vec2(0.0);
                    float spotlight = 0.0;

                    if (uPointerMode == 1.0) {          // bump
                        pos.z += influence * 12.0;
                        pos.z += sin(mDist * 0.5 - uTime * 5.0) * influence * 3.0;
                    } else if (uPointerMode == 2.0) {   // crater
                        pos.z -= influence * 14.0;
                        pos.z += sin(mDist * 0.5 - uTime * 5.0) * influence * 3.0;
                    } else if (uPointerMode == 3.0) {   // repel
                        pos.xy += dir * influence * 14.0;
                    } else if (uPointerMode == 4.0) {   // attract
                        pos.xy -= dir * min(influence * 10.0, mDist * 0.8);
                        pos.z += influence * 4.0;
                    } else if (uPointerMode == 5.0) {   // vortex
                        vec2 perp = vec2(-dir.y, dir.x);
                        pos.xy += perp * influence * 12.0;
                        pos.z += influence * 3.0;
                    } else if (uPointerMode == 6.0) {   // spotlight
                        spotlight = influence;
                    } else if (uPointerMode == 7.0) {   // ripples
                        float rip = smoothstep(uRadius * 2.0, 0.0, mDist) * uMouseStrength * uPower;
                        pos.z += sin(mDist * 0.6 - uTime * 6.0) * rip * 7.0;
                    } else if (uPointerMode == 8.0) {   // shake
                        float n1 = fract(sin(dot(position.xy, vec2(12.9898, 78.233))) * 43758.5453);
                        float n2 = fract(sin(dot(position.xy, vec2(39.3468, 11.135))) * 24634.6345);
                        pos.x += sin(uTime * 30.0 * (n1 + 0.5)) * influence * 3.0;
                        pos.y += cos(uTime * 27.0 * (n2 + 0.5)) * influence * 3.0;
                        pos.z += sin(uTime * 40.0 * (n1 + n2)) * influence * 5.0;
                    } else if (uPointerMode == 9.0) {   // wake
                        pos.xy += uMouseVel * influence * 3.0;
                        pos.z += length(uMouseVel) * influence * 1.5;
                    } else if (uPointerMode == 10.0) {  // halo
                        float targetR = uRadius * 0.6;
                        pos.xy += dir * (targetR - mDist) * influence * 1.2;
                        pos.z += influence * 2.0;
                    } else if (uPointerMode == 11.0) {  // blackhole
                        vec2 perp = vec2(-dir.y, dir.x);
                        pos.xy -= dir * min(influence * 12.0, mDist * 0.9);
                        pos.xy += perp * influence * 8.0;
                        pos.z -= influence * 12.0;
                    } else if (uPointerMode == 12.0) {  // twist
                        float ang = influence * 2.5;
                        float ca = cos(ang);
                        float sa = sin(ang);
                        vec2 rel = pos.xy - uMouse;
                        pos.xy = uMouse + vec2(rel.x * ca - rel.y * sa, rel.x * sa + rel.y * ca);
                    }

                    // ---- click / tap shockwaves (up to 5 stack and interfere) ----
                    for (int w = 0; w < 5; w++) {
                        float age = uTime - uClickTime[w];
                        if (age > 0.0 && age < 3.0) {
                            float cDist = distance(pos.xy, uClickPos[w]);
                            float ring = age * 45.0;
                            float band = exp(-pow(cDist - ring, 2.0) * 0.01) * exp(-age * 2.0);
                            pos.z += band * 18.0 * uPower;
                            spotlight += band * 0.5;
                        }
                    }

                    // ---- shop transition: dots leave the field, each flying to
                    // its own home on a product card (staggered per dot) ----
                    float cardM = 0.0;
                    float dissolve = 0.0;   // dissolve-mode: extra per-dot fade-out
                    if (uCardMix > 0.001) {
                        vec2 tgt = aCard;
                        if (uDest == 1.0) tgt = aBag;
                        else if (uDest == 2.0) tgt = aArc;
                        else if (uDest == 3.0) tgt = aAbt;
                        float hc = fract(sin(dot(position.xy, vec2(41.3, 17.7))) * 3571.17);

                        if (uTransMode == 1.0) {
                            // DISSOLVE: the ocean drains from dim → bright. Each dot's
                            // fade is delayed by its own brightness (wave crest height),
                            // so the brightest dots linger last. Everything clears by
                            // ~0.7 so the screen empties before the page fades in.
                            float bright = smoothstep(-11.0, 11.0, pos.z);
                            float delay = bright * 0.45 + hc * 0.12;
                            dissolve = clamp((uCardMix - delay) / 0.22, 0.0, 1.0);
                        } else if (uTransMode == 2.0) {
                            // MAGNETIC: dots hold, then get yanked hard to their card
                            // edge — a slow build to a sudden snap, like iron filings.
                            float local = clamp((uCardMix - hc * 0.28) / 0.72, 0.0, 1.0);
                            cardM = local * local * local * local;
                            pos.x = mix(pos.x, tgt.x, cardM);
                            pos.y = mix(pos.y, tgt.y, cardM);
                            pos.z *= (1.0 - cardM);
                        } else {
                            // CASCADE (default): compose top→bottom by target Y, so the
                            // page sweeps in as a wave. jitter softens the sweep line.
                            float top = clamp(0.5 - tgt.y / 480.0 + (hc - 0.5) * 0.12, 0.0, 1.0);
                            cardM = clamp((uCardMix - top * 0.7) / 0.3, 0.0, 1.0);
                            cardM = cardM * cardM * (3.0 - 2.0 * cardM);
                            pos.x = mix(pos.x, tgt.x, cardM);
                            pos.y = mix(pos.y, tgt.y, cardM);
                            pos.z *= (1.0 - cardM);
                        }
                    }

                    // brightness emulates a rolling wave of light:
                    //   • rides the geometric wave height (crests glow, troughs dim)
                    //   • PLUS a travelling brightness band sweeping diagonally across
                    //     at its own speed, so a luminous wave visibly moves over the
                    //     field even where the geometry is flat
                    //   • PLUS a faint per-dot sparkle for life
                    // because both waves travel, no dot stays dim — it all shimmers.
                    // uWaveAmt scales how much the brightness swings (the preset):
                    // 0 = flat/uniform, higher = stronger rolling wave + flicker
                    // WAVE CRASHING ON SHORE:
                    //  • troughs fall toward nothing (dark), crests rise toward lit
                    //  • the breaking crest erupts in white FOAM that flickers hard,
                    //    like bubbles/spray on a wave breaking on the shore
                    // (hsh, wave, band, crest, descending computed up top with the fold)
                    float amt = clamp(uWaveAmt, 0.0, 1.6);

                    // foam across a WIDE band of the crest, VIOLENT per-dot flicker,
                    // weighted onto the falling side (the break); peaks right as it folds
                    float foamZone = smoothstep(0.36, 0.76, crest) * (0.18 + 0.82 * descending);
                    float bubble = 0.5 + 0.5 * sin(uTime * 11.5 + hsh * 55.0)
                                       * (0.4 + 0.6 * sin(uTime * 6.0 + hsh * 19.0));
                    float crestFoam = foamZone * (0.45 + 0.55 * bubble);

                    // the dark troughs stay CALM now (no scattered glints) — instead
                    // a bit of flicker lives right at the PEAK/tip of the wave, so the
                    // shimmer sits on the high water, not the dead-dark valleys
                    float peakZone = smoothstep(0.74, 1.0, crest);
                    float peakFlick = peakZone * pow(0.5 + 0.5 * sin(uTime * 8.5 + hsh * 63.0), 2.0);

                    vFoam = clamp((crestFoam + peakFlick * 0.4) * min(amt, 1.3), 0.0, 1.3);

                    // base: troughs fall to near-nothing, crests lift
                    float base = mix(0.5 - amt * 0.34, 0.5 + amt * 0.22, crest);
                    vOpacity = clamp(base + vFoam * 0.85, 0.0, 1.0);
                    vOpacity += influence * 0.4 + spotlight * 0.8;

                    // intro: each dot fades in as it assembles into place
                    vOpacity *= asmFade;
                    vOpacity = mix(vOpacity, 0.85, cardM);
                    vOpacity *= (1.0 - dissolve);   // dissolve mode fades the dots out

                    // invert transition per dot
                    float hdI = fract(sin(dot(position.xy, vec2(12.9898, 78.233))) * 43758.5453);
                    if (uInvertMode == 1.0) {
                        float edge = mix(-280.0, 280.0, uInvert);
                        vInv = smoothstep(position.x - 25.0, position.x + 25.0, edge);
                    } else if (uInvertMode == 2.0) {
                        float rr = uInvert * 420.0;
                        float bandI = smoothstep(rr, rr - 90.0, distance(position.xy, uInvertCenter));
                        vInv = uInvert >= 0.999 ? 1.0 : step(hdI, bandI * 1.2);
                    } else {
                        // uniform dissolve: every dot flips at its own random
                        // threshold as uInvert sweeps 0→1 — no origin point, a
                        // grainy field-wide flip. slick + textured.
                        float w = 0.28;   // dissolve band width
                        vInv = smoothstep(hdI - w, hdI + w, uInvert);
                    }
                    // nav-hover hint: a dissolve patch under the hovered menu
                    // item, tinted to the accent. Computed in SCREEN space (each
                    // dot's projected NDC vs the item's NDC) so it lands exactly
                    // under the item no matter how the hover camera moves.
                    vec4 mvPosition = modelViewMatrix * vec4(pos, 1.0);
                    vec4 clip = projectionMatrix * mvPosition;

                    vHint = 0.0;
                    if (uHint > 0.01 && clip.w > 0.0) {
                        vec2 ndc = clip.xy / clip.w;
                        vec2 rel = ndc - uHintCenter;
                        // fill the whole item rectangle (padded a touch), with an
                        // organic wobbly edge + dither so it reads natural, not a box
                        vec2 dd = (uHintSize + 0.02) - abs(rel);
                        float inside = min(dd.x, dd.y);
                        inside += 0.014 * sin(ndc.x * 44.0 + ndc.y * 27.0)
                                + 0.009 * sin(ndc.y * 68.0 - ndc.x * 19.0);
                        float band = smoothstep(-0.008, 0.05, inside) * uHint;
                        vHint = step(hdI * 0.8, band) * band;
                    }
                    // neutral hover: the smallest orange tint follows the cursor
                    vGlow = clamp(influence, 0.0, 1.0);

                    float sizeBoost = influence * 2.0 + spotlight * 3.0 + vHint * 1.5;
                    gl_PointSize = mix((2.5 + sizeBoost) * (100.0 / -mvPosition.z), 4.0 + sizeBoost, vHalf);
                    gl_Position = clip;
                }
            `,
            fragmentShader: `
                uniform vec3 uColor;
                uniform float uMode;
                varying float vOpacity;
                varying vec2 vUv;
                varying float vHalf;
                varying float vInv;
                varying float vHint;
                varying float vGlow;
                varying float vFoam;
                uniform vec3 uAccent;

                void main() {
                    vec2 cxy = 2.0 * gl_PointCoord - 1.0;
                    float r = dot(cxy, cxy);
                    if (r > 1.0) discard;

                    float finalAlpha = vOpacity;
                    float noise = sin(vUv.x * 20.0) * cos(vUv.y * 20.0);
                    finalAlpha *= mix(1.0, noise * 0.5 + 0.5, vHalf);
                    finalAlpha = min(1.0, finalAlpha + vHint * 0.6);

                    vec3 col = mix(uColor, vec3(0.05), vInv);
                    // full accent under a hovered menu item; a faint tint near the cursor otherwise
                    col = mix(col, uAccent, clamp(vHint + vGlow * 0.16, 0.0, 1.0));
                    // foam + sparkle glints whiten all the way to pure white
                    col = mix(col, vec3(1.0), clamp(vFoam, 0.0, 1.0));
                    gl_FragColor = vec4(col, finalAlpha);
                }
            `,
            transparent: true,
            depthWrite: false,
            blending: THREE.AdditiveBlending
        });

        // Grid sized from the camera frustum so it always covers the screen.
        let points = null;
        let visibleWidth = 0, visibleHeight = 0;

        // product-card rects as viewport fractions — the particle targets and
        // the DOM cards both derive from this single source of truth
        // shop is an edge-to-edge brutalist lattice (from the store's own Dawn
        // CSS): 5-up, fixed 3:4 cells starting under the header. cell height
        // is viewport-dependent, so fractions are computed inside buildGrid.
        const SHOP_TOP = 0.12;
        // cart page: one wide bag panel, a summary block, a checkout block
        const BAG_FRACS = [
            { x: 0.08, y: 0.16, w: 0.55, h: 0.66 },
            { x: 0.68, y: 0.16, w: 0.24, h: 0.42 },
            { x: 0.68, y: 0.64, w: 0.24, h: 0.18 }
        ];
        // archive page: a dense index of full-width rows
        const ARC_FRACS = [0, 1, 2, 3, 4].map(i => ({ x: 0.08, y: 0.16 + i * 0.14, w: 0.84, h: 0.115 }));
        // about page: statement column + contact block
        const ABT_FRACS = [
            { x: 0.08, y: 0.16, w: 0.50, h: 0.60 },
            { x: 0.62, y: 0.16, w: 0.30, h: 0.34 }
        ];

        function buildGrid() {
            visibleHeight = 2 * Math.tan(THREE.MathUtils.degToRad(camera.fov / 2)) * CAM_Z;
            visibleWidth = visibleHeight * camera.aspect;

            const gridW = visibleWidth * OVERSCAN;
            const gridH = visibleHeight * OVERSCAN;
            const countX = Math.ceil(gridW / SPACING);
            const countY = Math.ceil(gridH / SPACING);

            const geometry = new THREE.BufferGeometry();
            const positions = new Float32Array(countX * countY * 3);
            const uvs = new Float32Array(countX * countY * 2);

            let i = 0, j = 0;
            for (let ix = 0; ix < countX; ix++) {
                for (let iy = 0; iy < countY; iy++) {
                    positions[i] = (ix - countX / 2) * SPACING;
                    positions[i + 1] = (iy - countY / 2) * SPACING;
                    positions[i + 2] = 0;

                    uvs[j] = ix / countX;
                    uvs[j + 1] = iy / countY;

                    i += 3;
                    j += 2;
                }
            }

            // page targets: each dot gets a home on one of the layout rects —
            // most on the border outline, the rest scattered inside. built once
            // per destination (shop cards, cart page) from its rect fractions.
            const fract2 = (x) => x - Math.floor(x);
            const buildTargets = (fracs) => {
                const rects = fracs.map(f => ({
                    x: (f.x - 0.5) * visibleWidth,
                    y: (0.5 - f.y - f.h) * visibleHeight,
                    w: f.w * visibleWidth,
                    h: f.h * visibleHeight
                }));
                const arr = new Float32Array(countX * countY * 2);
                for (let k = 0, ci = 0; k < countX * countY; k++) {
                    const r = rects[k % rects.length];
                    const h1 = fract2(Math.sin(k * 12.9898) * 43758.5453);
                    const h2 = fract2(Math.sin(k * 78.2330) * 12543.1230);
                    const h3 = fract2(Math.sin(k * 37.7190) * 26711.7700);
                    let tx, ty;
                    if (h1 < 0.68) {
                        const p = h2 * 2 * (r.w + r.h);
                        if (p < r.w)                 { tx = r.x + p; ty = r.y; }
                        else if (p < r.w + r.h)      { tx = r.x + r.w; ty = r.y + (p - r.w); }
                        else if (p < 2.0 * r.w + r.h){ tx = r.x + (p - r.w - r.h); ty = r.y + r.h; }
                        else                         { tx = r.x; ty = r.y + (p - 2 * r.w - r.h); }
                    } else {
                        tx = r.x + h3 * r.w;
                        ty = r.y + h2 * r.h;
                    }
                    arr[ci++] = tx;
                    arr[ci++] = ty;
                }
                return arr;
            };

            geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
            geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
            // 3:4 portrait cells, 1/5 viewport wide, flush against each other
            const cellH = Math.min(0.75, 0.2 * (4 / 3) * (visibleWidth / visibleHeight));
            const SHOP_FRACS = [0, 1, 2, 3].map(i => ({ x: i * 0.2, y: SHOP_TOP, w: 0.2, h: cellH }));
            geometry.setAttribute('aCard', new THREE.BufferAttribute(buildTargets(SHOP_FRACS), 2));
            geometry.setAttribute('aBag', new THREE.BufferAttribute(buildTargets(BAG_FRACS), 2));
            geometry.setAttribute('aArc', new THREE.BufferAttribute(buildTargets(ARC_FRACS), 2));
            geometry.setAttribute('aAbt', new THREE.BufferAttribute(buildTargets(ABT_FRACS), 2));

            if (points) {
                points.geometry.dispose();
                scene.remove(points);
            }
            points = new THREE.Points(geometry, material);
            scene.add(points);
        }
        buildGrid();

        let hoverMenuIdx = -1;                  // which menu item the cursor is on
        let hoverItemEl = null;                 // the hovered item element (anchors the hint patch)
        const hintCenterTarget = new THREE.Vector2(0, 0);
        const hintCenterSmooth = new THREE.Vector2(0, 0);
        const hintSizeTarget = new THREE.Vector2(0.1, 0.03);
        const hintSizeSmooth = new THREE.Vector2(0.1, 0.03);
        let lastHintIdx = -1;     // detects item change to re-trigger the orange fade
        let hintSnap = false;     // snap the hint to the new item on change

        // ---------- the field's settings ----------
        // Fixed. They used to be tunable from a hidden dev panel and saved per browser, which let a
        // tuned browser drift from what every visitor sees; the panel is gone, so these are the look.
        //   wave 1.6   the ocean preset (strong flicker)
        //   trans 1    DISSOLVE: keep the invert, then the field drains out dim→bright and the
        //              cards fade in — no composing dots into card outlines
        const settings = {
            field: '1', cursor: '1', layout: '1', radius: 30, power: 100,
            menuFx: 'field', invertFx: 'fade', wave: '1.6', trans: '1', pageMesh: 'off'
        };

        // ---------- pointer (mouse + touch) ----------
        const mouseTarget = new THREE.Vector2(0, 0);
        const mouseSmooth = new THREE.Vector2(0, 0);
        let strengthTarget = 0;
        let clickSlot = 0;
        let fieldSX = -1, fieldSY = -1;   // last cursor screen pos for live-camera reprojection
        const pointerNdc = new THREE.Vector2(0, 0);

        function toWorld(clientX, clientY, out) {
            const nx = (clientX / window.innerWidth) * 2 - 1;
            const ny = -((clientY / window.innerHeight) * 2 - 1);
            out.set(nx * (visibleWidth / 2), ny * (visibleHeight / 2));
            return { nx, ny };
        }

        function overUi(e) {
            return e.target.closest && (e.target.closest('.menu') || e.target.closest('.hot') || e.target.closest('#player'));
        }
        // narrower: only the player. The field keeps reacting under the menu
        // (and page content) so the hover follows onto the nav items.
        function overChrome(e) {
            return e.target.closest && e.target.closest('#player');
        }

        /* The field stops reading the pointer in two situations.
           During a page transition (shop-mode), so the invert/morph reads as a deliberate
           animation rather than a last-second hover still chasing the mouse.
           And while the cart window is up. The cart is meant to feel like something lifted off
           the site and held in front of it, so the mesh behind it holds still — dragging the
           window by its title bar shouldn't send ripples through the field underneath, which is
           what tied the two back together and made the cart feel like part of the same surface. */
        function fieldInert() {
            const c = document.body.classList;
            // note-open covers any small window lifted over the page (the motion note, the
            // easter egg's question); inno-game is the game screen itself, which is opaque
            return c.contains('shop-mode') || c.contains('cart-win-open') ||
                   c.contains('note-open') || c.contains('inno-game');
        }

        window.addEventListener('pointermove', (e) => {
            if (overChrome(e)) return;
            if (fieldInert()) { strengthTarget = 0; return; }
            const { nx, ny } = toWorld(e.clientX, e.clientY, mouseTarget);
            pointerNdc.set(nx, ny);
            fieldSX = e.clientX; fieldSY = e.clientY;   // re-projected each frame via the live camera
            strengthTarget = 1;
        });

        window.addEventListener('pointerdown', (e) => {
            if (overUi(e)) return;
            // no shockwave under the cart window either — including the one a drag on its title
            // bar would otherwise fire on every grab
            if (fieldInert()) { strengthTarget = 0; return; }
            const { nx, ny } = toWorld(e.clientX, e.clientY, mouseTarget);
            screenToPlane(e.clientX, e.clientY, mouseTarget);   // live-camera accurate click point
            pointerNdc.set(nx, ny);
            fieldSX = e.clientX; fieldSY = e.clientY;
            strengthTarget = 1;
            if (e.pointerType === 'touch') mouseSmooth.copy(mouseTarget);
            // rotate through 5 shockwave slots so rapid clicks stack
            clickSlot = (clickSlot + 1) % 5;
            material.uniforms.uClickPos.value[clickSlot].copy(mouseTarget);
            material.uniforms.uClickTime.value[clickSlot] = material.uniforms.uTime.value;
        });

        window.addEventListener('pointerup', (e) => {
            if (e.pointerType === 'touch') strengthTarget = 0;
        });
        document.addEventListener('mouseleave', () => { strengthTarget = 0; });

        // ---------- gyro tilt ----------
        const tiltNdc = new THREE.Vector2(0, 0);       // eased value fed to the field
        const tiltTarget = new THREE.Vector2(0, 0);    // raw sensor reading
        const tiltVel = new THREE.Vector2(0, 0);       // the ease carries momentum — see springTilt

        /* SOFT AT BOTH ENDS. A hard deadzone — "under 0.04, call it zero" — is a step, and a
           phone spends most of its life near level, right on top of it: the smallest thing a
           hand does snaps the swing on and off, which is most of what reads as jolt. This fades
           in instead, reaching the deadzone's edge with zero value AND zero slope, so the move
           grows out of nothing rather than starting. The same curve flattens again as it
           approaches a full lean, so the far end arrives rather than hits a wall. */
        const TILT_DEADZONE = 0.06;
        function shapeTilt(v) {
            const a = Math.abs(v);
            if (a <= TILT_DEADZONE) return 0;
            const t = Math.min(1, (a - TILT_DEADZONE) / (1 - TILT_DEADZONE));
            // quintic, not cubic: zero slope AND zero curvature at both ends, so the swing does
            // not just start and stop gently, it stops CHANGING gently — no seam at either limit
            return (v < 0 ? -1 : 1) * t * t * t * (t * (t * 6 - 15) + 10);
        }

        /* A DAMPED SPRING, not a lerp. `x += (target - x) * k` is the usual ease and it has a
           hidden corner in it: the moment the target moves, the output leaves at its highest
           speed and decays from there. Every new reading therefore begins with a yank — and a
           gyro delivers a new reading sixty times a second. A spring has to accelerate into the
           move, so velocity is continuous through it and the speed is never uniform: it gathers,
           carries, and eases off as it arrives.

           Two numbers shape that. OMEGA is how eager it is — lower is a longer, slower, more
           liquid glide. ZETA is how it lands: at 1 it is critically damped and creeps the last
           few percent, which is technically perfect and feels slightly dead. Under 1 it arrives
           with a settle instead of asymptoting into place. The overshoot is exp(-πζ/√(1-ζ²)):
           0.9 gives 0.15%, which on a 41° swing is a sixteenth of a degree — true to the theory
           and invisible in the hand. 0.78 gives about 2%, call it three quarters of a degree,
           which is the weight you feel when the mesh comes to rest rather than simply stopping.
           Two oscillations would be a bounce; this is one, and it is nearly over when it starts.

           Backward Euler, so it is unconditionally stable: a long frame slows it, never explodes
           it. Solved for the new velocity first, then the position follows it. */
        const TILT_OMEGA = 2.9;   // eagerness
        const TILT_ZETA = 0.78;   // 1 = no overshoot at all; below it, weight
        function springTilt(dt) {
            const k = TILT_OMEGA * TILT_OMEGA;          // stiffness
            const c = 2 * TILT_ZETA * TILT_OMEGA;       // damping
            const det = 1 / (1 + dt * c + dt * dt * k);
            tiltVel.x = (tiltVel.x + dt * k * (tiltTarget.x - tiltNdc.x)) * det;
            tiltVel.y = (tiltVel.y + dt * k * (tiltTarget.y - tiltNdc.y)) * det;
            tiltNdc.x += dt * tiltVel.x;
            tiltNdc.y += dt * tiltVel.y;
        }
        const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
        // true only for real pointing devices — gates hover-only card behaviour
        const canHover = !!(window.matchMedia && window.matchMedia('(hover: hover)').matches);
        /* THE DESKTOP LAYOUT, as the stylesheets define it: the phone rules are max-width:600px,
           so "desktop" here means 601px and up — the layout on screen, not a guess about the
           device. Read at the moment it matters rather than once, so a window dragged across
           the line behaves like the layout it has just become. */
        const deskLayout = window.matchMedia ? window.matchMedia('(min-width: 601px)') : { matches: false };
        let gyroOn = false;

        // No on-screen tilt button any more — it's hinted in the boot readout and simply discovered
        // by tilting. Android needs no permission, so it just starts; iOS requires a user gesture,
        // so the first tap anywhere doubles as the prompt (once, then it stops listening).
        let gyroEvents = 0;
        if (isTouchDevice && typeof DeviceOrientationEvent !== 'undefined') {
            if (typeof DeviceOrientationEvent.requestPermission !== 'function') {
                startGyro();                                   // Android / older iOS: no gate
            } else {
                // iOS 13+ gates motion behind a prompt that must come from a user gesture.
                // This used to be a ONE-SHOT listener on the first pointerdown anywhere — so the
                // prompt landed on whatever tap the visitor made first, usually the tap meant for
                // SHOP. Dismissing it (or tapping through) burned the only attempt and the gyro
                // was gone for the session. Now: never ask on a tap aimed at a control, and keep
                // offering on later taps until it's granted or we've asked a few times.
                // Ask on ANY gesture, control taps included. The previous pass skipped taps that
                // landed on a control so the prompt wouldn't collide with a navigation — but on
                // the homescreen almost every tap IS a control (SHOP / VISIT / INDEX / the
                // player), so the request simply never ran and the gyro never turned on.
                // `touchend`/`pointerup` rather than `pointerdown`: both count as user gestures
                // for the permission API, and firing on release lets the tap's own action settle
                // first. Keeps offering on later gestures until granted, up to a few tries.
                let asks = 0;
                let asking = false;
                const GEST = ['touchend', 'pointerup', 'click'];
                const askGyro = (ev) => {
                    if (gyroOn || asks >= 8) { GEST.forEach(g => window.removeEventListener(g, askGyro, true)); return; }
                    /* The HOME SCREEN only. Motion is only ever used there, so a visitor who taps
                       straight through to SHOP isn't asked on the way out, or anywhere inside the
                       pages; the question waits for their next tap back on the home screen. */
                    if (shopPhase !== 'home') return;
                    if (ev && ev.target && ev.target.closest && ev.target.closest('.menu, [data-goto], a[href]')) return;
                    // one tap fires touchend, pointerup AND click — one request per tap, not three
                    if (asking) return;
                    asking = true;
                    asks++;
                    const askedAt = performance.now();
                    try {
                        DeviceOrientationEvent.requestPermission()
                            .then(state => {
                                asking = false;
                                const granted = state === 'granted';
                                if (granted) startGyro();
                                // the BIOS readout waits on this to say where it stands, and to carry on
                                document.dispatchEvent(new CustomEvent('inno:motion', { detail: { granted: granted } }));
                                // a definitive answer either way ends the asking
                                GEST.forEach(g => window.removeEventListener(g, askGyro, true));
                            })
                            // outside a gesture (the boot's hand-over) this rejects — free the next tap to ask
                            .catch(() => { asking = false; });
                    } catch (err) { asking = false; }
                };
                // capture phase so a handler that stops propagation can't hide the gesture
                GEST.forEach(g => window.addEventListener(g, askGyro, true));
                // The BOOT screen is the first thing on screen and it already invites a tap, so
                // that tap is the earliest legitimate moment to ask — iOS will not hand out
                // motion permission without a user gesture, so "on page load" is not available
                // to us. Asking there gets the prompt in before the visitor reaches for SHOP,
                // which is what made it feel like it never appeared.
                window.innoAskGyro = askGyro;
            }
        }

        /* ---------- the readout rakes AGAINST the field ----------
           The mesh leans with the phone: tilt left and the camera tracks left. The status block
           in the top-right does the opposite, and its three lines do it by different amounts —
           [ sys:ok ] barely, the clock a little, the coordinates most — so a tilt pulls the block
           apart into three plates at three depths instead of sliding it as one piece.

           The counter direction is the whole point. Two things answering the same gesture in
           opposite senses reads as two different surfaces at two different distances, which is
           something neither of them says on its own.

           Gyro only, and no gating needed for that: with no sensor tiltNdc never leaves zero, so
           on a desktop nothing here moves and nothing gets written. */
        const TILT_PX = 15;                     // travel of the deepest plate at full deflection
        const TILT_RISE = 0.55;                 // the gesture is left/right; vertical stays quieter

        /* Each registered plate rakes by its own depth. Both corners run the same formula, so
           they move as one system — each raking away from the corner it is anchored to. */
        const tiltPlates = [];
        function tiltRegister(sel, depths, lift) {
            const els = document.querySelectorAll(sel);
            for (let i = 0; i < els.length; i++) {
                tiltPlates.push({ el: els[i], d: depths[i] === undefined ? 1 : depths[i], lift: !!lift });
            }
        }
        // top right: three lines, depth increasing away from the corner
        tiltRegister('.status .st-line', [0.40, 0.72, 1.0], true);
        /* Anything that arrives later can join the rake — the BIOS readout does, line by line, so
           the intro leans with the phone exactly as the homescreen does. Plates whose element has
           left the DOM (the boot, once it's gone) drop out on the next write. */
        window.innoTiltPlate = function (el, depth, lift) {
            if (!el) return false;
            for (let i = 0; i < tiltPlates.length; i++) if (tiltPlates[i].el === el) return false;
            tiltPlates.push({ el: el, d: depth === undefined ? 1 : depth, lift: !!lift });
            return true;
        };
        /* bottom left: the transport nearer, the cookies token further.
           Two plates and not five, because #player draws its own `[ … ]` as pseudo-elements —
           raking its children individually would slide the controls out from between their own
           brackets. And no brightness lift down here: these carry :hover, :focus-visible and
           .playing in opacity, and an inline write would outrank every one of them. */
        tiltRegister('#player, #cookie-consent', [0.45, 1.0], false);
        // read live rather than latched at boot, so toggling the OS setting takes effect without
        // a reload; tiltForce is the console hook's override (there is no other way to see the
        // effect on a machine that reports reduced motion)
        const tiltMQ = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
        let tiltForce = false;
        function tiltMuted() { return !tiltForce && !!(tiltMQ && tiltMQ.matches); }
        let tiltWroteX = 0, tiltWroteY = 0, tiltEverWrote = false;
        function updateTiltText() {
            if (!tiltPlates.length || tiltMuted()) return;
            // a change too small to see is not worth a style write per line per frame
            if (tiltEverWrote &&
                Math.abs(tiltNdc.x - tiltWroteX) < 0.004 &&
                Math.abs(tiltNdc.y - tiltWroteY) < 0.004) return;
            tiltWroteX = tiltNdc.x; tiltWroteY = tiltNdc.y; tiltEverWrote = true;
            const mag = Math.min(1, Math.hypot(tiltNdc.x, tiltNdc.y));
            for (let i = 0; i < tiltPlates.length; i++) {
                const p = tiltPlates[i];
                if (!p.el.isConnected) { tiltPlates.splice(i--, 1); continue; }   // the boot's lines, once it's gone
                const tx = -tiltNdc.x * TILT_PX * p.d;
                const ty = -tiltNdc.y * TILT_PX * TILT_RISE * p.d;
                p.el.style.transform = 'translate(' + tx.toFixed(2) + 'px,' + ty.toFixed(2) + 'px)';
                // the further a line travels the brighter it sits, so the rake reads as depth
                // rather than as lines that have simply fallen out of alignment
                if (p.lift) p.el.style.opacity = (0.55 + mag * p.d * 0.33).toFixed(3);
            }
        }
        // lets the effect be judged on a desktop, where there is no sensor to tilt (and overrides
        // the reduced-motion gate for the same reason — it is a console hook, never a code path)
        window.innoTiltText = (x, y) => {
            tiltForce = true;
            tiltTarget.set(x || 0, y || 0);
            tiltNdc.set(tiltTarget.x, tiltTarget.y);   // snap rather than wait out the ease
            tiltVel.set(0, 0);                         // and arrive stationary, not mid-swing
            updateTiltText();
            // says what it did — id or class, since the dock plates are id-addressed
            return tiltPlates.map((p) => (p.el.id || p.el.className) + ': ' + p.el.style.transform);
        };

        let baseG = 0, baseB = 45, calibN = 0;   // the resting orientation, learned on the first readings
        let smoothG = 0, smoothB = 45, evAt = 0; // the reading itself, low-passed before anything uses it
        function startGyro() {
            if (gyroOn) return;
            gyroOn = true;
            document.documentElement.classList.add('inno-tilt');
            window.addEventListener('deviceorientation', (e) => {
                // `|| 0` on gamma silently swallowed a null reading as a valid centre, so a
                // device reporting nothing looked identical to one held flat — counting real
                // events is what tells the two apart (see window.innoGyro).
                if (e.gamma == null && e.beta == null) return;
                gyroEvents++;
                const rawG = e.gamma || 0, rawB = (e.beta == null ? 45 : e.beta);
                /* CENTRED ON HOW THE PHONE IS ACTUALLY HELD, not on dead flat. Roll was measured
                   against zero and pitch against a guessed 45°, so anyone reading at their own
                   natural angle began the page already leaning: the travel left to reach full
                   deflection was shorter one way than the other, and the field swung visibly
                   further to one side. The first few readings set the origin instead — averaged,
                   because a single sample taken mid-movement would enshrine a wobble. */
                if (calibN < 6) {
                    calibN++;
                    baseG += (rawG - baseG) / calibN;
                    baseB += (rawB - baseB) / calibN;
                    if (calibN === 1) { smoothG = rawG; smoothB = rawB; }   // start settled, not sliding in
                }
                /* A light low-pass on the reading itself, ahead of everything else. The sensor
                   is quantised and jittery by a few tenths of a degree, and feeding that
                   directly to a spring means driving it with noise — the spring is smooth, but
                   it faithfully smooths a wobble. 45ms is short enough to be imperceptible as
                   lag and long enough to take the fizz off. Time-based, because the event rate
                   is not the same on every handset. */
                const evNow = performance.now();
                const evDt = evAt ? Math.min(0.1, (evNow - evAt) / 1000) : 0.016;
                evAt = evNow;
                const k = 1 - Math.exp(-evDt / 0.045);
                smoothG += (rawG - smoothG) * k;
                smoothB += (rawB - smoothB) * k;
                // ÷20 ≈ full deflection near 20° OF LEAN, then shaped so it eases in and out
                let gx = shapeTilt((smoothG - baseG) / 20);
                let gy = shapeTilt((smoothB - baseB) / 20);
                /* Clamped as a PAIR, not one axis at a time. Full deflection on both at once
                   sits at √2 of the envelope the orbit limits were solved against, which is the
                   corner where the grid's far edge comes into frame. */
                const mag = Math.hypot(gx, gy);
                if (mag > 1) { gx /= mag; gy /= mag; }
                tiltTarget.x = gx;
                tiltTarget.y = gy;
            });
        }
        // lets the state be checked from the console on a real handset, where the only other
        // symptom of a failed gyro is "the field doesn't move", which proves nothing
        // if the sensor is live but the field looks static, this says which half is at fault
        window.innoGyroTest = (x, y) => {
            // clamped like a real reading, so a test lean can't reach anywhere the sensor can't
            let tx = x || 0, ty = y || 0; const m = Math.hypot(tx, ty);
            if (m > 1) { tx /= m; ty /= m; }
            tiltTarget.set(tx, ty);
            return 'tiltTarget set — the field should lean';
        };
        window.innoGyro = () => ({
            started: gyroOn,
            events: gyroEvents,
            needsPermission: typeof DeviceOrientationEvent !== 'undefined' &&
                             typeof DeviceOrientationEvent.requestPermission === 'function',
            touch: isTouchDevice,
            tilt: { x: +tiltTarget.x.toFixed(3), y: +tiltTarget.y.toFixed(3) }
        });

        // ---------- crosshair cursor ----------
        const cursorEl = document.getElementById('cursor');
        const cursorInner = cursorEl.querySelector('.inner');
        // the crosshair trails the pointer by a hair (eased in animate) —
        // just enough lag to feel deliberate rather than mechanical
        let cursorTX = -100, cursorTY = -100;
        let cursorX = -100, cursorY = -100;
        const coordEls = document.querySelectorAll('.coords');
        const wordmarkEls = document.querySelectorAll('.logo.wordmark');   // mouse-reactive drift

        /* ---------- the x/y readout ----------
           This used to be written from the `mousemove` handler INSIDE the `pointer: fine` branch,
           so on a phone — where the field is driven entirely by touch — it sat at `x 0000 y 0000`
           for the whole session. Pointer events cover mouse, pen and touch alike, so the readout
           now comes off the same stream that drives the field, and it stays silent in exactly the
           conditions the field does: what it reports is the mesh's input, not the pointer's.

           A tap LATCHES. The value freezes at the point you touched and holds until the next
           input, because on a touch screen the coordinate you were aiming for is gone the instant
           you lift your finger. "The next input" deliberately isn't "any pointer event at all":
           a drag BEGINS with a pointerdown, so releasing on the first move would unlatch before
           the number could be read. It takes a real move — RELEASE_PX away from the latched
           point — or a fresh tap somewhere else. */
        const RELEASE_PX = 18;
        let coordHeld = false, coordHX = 0, coordHY = 0;
        const c4 = (n) => String(Math.max(0, Math.round(n))).padStart(4, '0');
        function writeCoords(x, y) {
            // non-breaking spaces: the gap between the two pairs is part of the readout's shape,
            // and a run of ordinary spaces collapses to one
            const s = 'x ' + c4(x) + '  y ' + c4(y);
            for (let ce = 0; ce < coordEls.length; ce++) coordEls[ce].textContent = s;
        }
        function lockCoords(on) {
            coordHeld = on;
            for (let ce = 0; ce < coordEls.length; ce++) coordEls[ce].classList.toggle('coords-lock', on);
        }
        window.addEventListener('pointermove', (e) => {
            if (fieldInert()) return;
            if (coordHeld) {
                if (Math.abs(e.clientX - coordHX) < RELEASE_PX &&
                    Math.abs(e.clientY - coordHY) < RELEASE_PX) return;
                lockCoords(false);
            }
            writeCoords(e.clientX, e.clientY);
        }, { passive: true });
        window.addEventListener('pointerdown', (e) => {
            if (fieldInert()) return;
            coordHX = e.clientX; coordHY = e.clientY;
            writeCoords(e.clientX, e.clientY);
            lockCoords(true);
        }, { passive: true });

        // ---- contrast cursor: flip the crosshair black↔white so it stays readable
        //      over whatever's beneath it — a product image (sampled), a bone overlay,
        //      or the dark field. no accent colour. ----
        const curSampleC = document.createElement('canvas');
        const curSampleX = curSampleC.getContext && curSampleC.getContext('2d');
        let lastCurT = 0;
        function updateCursorContrast(x, y) {
            let lum = 0.06;                      // default: the dark void field → white cursor
            const els = document.elementsFromPoint(x, y);
            for (let k = 0; k < els.length; k++) {
                const el = els[k];
                if (el.id === 'cursor' || (el.closest && el.closest('#cursor'))) continue;
                if (el.tagName === 'IMG' && el.naturalWidth && curSampleX) {
                    try {
                        const rct = el.getBoundingClientRect();
                        const rx = Math.min(0.999, Math.max(0, (x - rct.left) / rct.width));
                        const ry = Math.min(0.999, Math.max(0, (y - rct.top) / rct.height));
                        curSampleC.width = 1; curSampleC.height = 1;
                        curSampleX.drawImage(el, rx * el.naturalWidth, ry * el.naturalHeight, 1, 1, 0, 0, 1, 1);
                        const p = curSampleX.getImageData(0, 0, 1, 1).data;
                        lum = (0.2126 * p[0] + 0.7152 * p[1] + 0.0722 * p[2]) / 255;
                    } catch (e) { /* tainted → keep default */ }
                    break;
                }
                const bg = getComputedStyle(el).backgroundColor;
                const m = bg.match(/rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)(?:,\s*([\d.]+))?\)/);
                if (m && (m[4] === undefined || parseFloat(m[4]) > 0.5)) {
                    lum = (0.2126 * +m[1] + 0.7152 * +m[2] + 0.0722 * +m[3]) / 255;
                    break;
                }
                if (el.id === 'webgl-container' || el.tagName === 'CANVAS') { lum = 0.06; break; }
            }
            cursorEl.classList.toggle('cur-dark', lum > 0.5);   // light under cursor → black cursor
        }

        if (window.matchMedia('(pointer: fine)').matches) {
            document.documentElement.classList.add('inno-cursor');   // hide the OS cursor site-wide

            // ---- easter egg: a fast cursor flick leaves a short ASCII trail (glitch chars,
            //      kept in our palette via CSS) that flickers characters and fades over ~1s ----
            const TRAIL_CH = '!<>-_\\/[]{}=+*^?#%&01';
            let trLastX = -1, trLastY = -1, trLastT = 0, trLastSpawn = 0;
            function spawnTrail(x, y) {
                const s = document.createElement('span');
                s.className = 'inno-trail';
                s.textContent = TRAIL_CH[(Math.random() * TRAIL_CH.length) | 0];
                s.style.left = x + 'px'; s.style.top = y + 'px';
                document.body.appendChild(s);
                let flips = 0;
                const iv = setInterval(() => {
                    s.textContent = TRAIL_CH[(Math.random() * TRAIL_CH.length) | 0];
                    if (++flips > 6) clearInterval(iv);
                }, 70);
                setTimeout(() => { clearInterval(iv); s.remove(); }, 1000);
            }

            window.addEventListener('mousemove', (e) => {
                cursorTX = e.clientX;
                cursorTY = e.clientY;
                cursorEl.style.opacity = 1;

                // velocity → a fast flick drops a trail char (throttled so it's a trail, not a flood)
                const trNow = e.timeStamp;
                if (trLastX >= 0) {
                    const vdx = e.clientX - trLastX, vdy = e.clientY - trLastY, vdt = (trNow - trLastT) || 16;
                    if (Math.hypot(vdx, vdy) / vdt > 1.7 && trNow - trLastSpawn > 55) { spawnTrail(e.clientX, e.clientY); trLastSpawn = trNow; }
                }
                trLastX = e.clientX; trLastY = e.clientY; trLastT = trNow;

                // (the x/y readout is driven from the pointer handlers above, which cover touch
                //  too — writing it from here as well would overwrite a latched value on the
                //  first mouse move after a click)

                // the wordmark leans a few px toward the pointer — subtle, magnetic. Not on a desk,
                // where it is set plainly and holds still (see the wordmark note in index.liquid)
                if (deskLayout.matches) {
                    for (let wi = 0; wi < wordmarkEls.length; wi++) if (wordmarkEls[wi].style.transform) wordmarkEls[wi].style.transform = '';
                } else {
                    const wdx = (e.clientX / window.innerWidth - 0.5);
                    const wdy = (e.clientY / window.innerHeight - 0.5);
                    for (let wi = 0; wi < wordmarkEls.length; wi++) {
                        wordmarkEls[wi].style.transform =
                            'translate(' + (wdx * 12).toFixed(1) + 'px,' + (wdy * 8).toFixed(1) + 'px)';
                    }
                }

                // flip the cursor black/white against whatever's under it (throttled)
                if (e.timeStamp - lastCurT > 55) { lastCurT = e.timeStamp; updateCursorContrast(e.clientX, e.clientY); }

                const hot = !!(e.target.closest && e.target.closest('a, button, input, .chip, .menu'));
                cursorEl.classList.toggle('hot', hot);
            });
            document.addEventListener('mouseleave', () => {
                cursorEl.style.opacity = 0;
            });
            // scale-up on click for tactile feedback; held briefly so a fast click
            // (mousedown+up in <100ms) still shows the pop
            let clickHold;
            // capture phase so a child's stopPropagation can never swallow the pop
            window.addEventListener('pointerdown', () => { cursorEl.classList.add('clicking'); clearTimeout(clickHold); }, true);
            window.addEventListener('pointerup', () => { clearTimeout(clickHold); clickHold = setTimeout(() => cursorEl.classList.remove('clicking'), 160); }, true);
            window.addEventListener('pointercancel', () => { clearTimeout(clickHold); cursorEl.classList.remove('clicking'); }, true);
        } else {
            cursorEl.style.display = 'none';
        }

        // ---------- text scramble on hover (aino-style decode) ----------
        // Each text node is wrapped in a span that remembers its original text;
        // on hover the letters cycle through glitch glyphs and resolve
        // left-to-right back to the real string.
        const SCR_CHARS = '!<>-_\\/[]{}=+*^?#%&';
        function wrapScrambleTargets(root) {
            const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
                acceptNode(node) {
                    if (!node.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
                    if (node.parentElement.closest('.clock, .coords')) return NodeFilter.FILTER_REJECT;   // live readouts rewrite themselves
                    if (node.parentElement.closest('.menu-ig')) return NodeFilter.FILTER_REJECT;          // the handle is a link, not a readout
                    if (node.parentElement.dataset.orig !== undefined) return NodeFilter.FILTER_REJECT;
                    return NodeFilter.FILTER_ACCEPT;
                }
            });
            const nodes = [];
            while (walker.nextNode()) nodes.push(walker.currentNode);
            nodes.forEach(node => {
                const span = document.createElement('span');
                span.dataset.orig = node.nodeValue;
                node.parentNode.insertBefore(span, node);
                span.appendChild(node);
            });
        }

        function scramble(span) {
            const orig = span.dataset.orig;
            if (span._raf) cancelAnimationFrame(span._raf);
            const start = performance.now();
            const dur = 380 + orig.length * 18;
            const step = (now) => {
                const p = (now - start) / dur;
                if (p >= 1) { span.textContent = orig; span._raf = null; return; }
                let s = '';
                for (let i = 0; i < orig.length; i++) {
                    const c = orig[i];
                    // resolved characters stay; unresolved ones keep cycling
                    if (c === ' ' || p * orig.length * 1.6 > i) s += c;
                    else s += SCR_CHARS[(Math.random() * SCR_CHARS.length) | 0];
                }
                span.textContent = s;
                span._raf = requestAnimationFrame(step);
            };
            span._raf = requestAnimationFrame(step);
        }

        document.querySelectorAll('.menu-item, .layout .logo, .layout .status, .layout .side').forEach(host => {
            wrapScrambleTargets(host);
            const isMark = host.classList.contains('logo');
            host.addEventListener('mouseenter', () => {
                if (isMark && deskLayout.matches) return;       // the desktop wordmark is a still signature
                host.querySelectorAll('[data-orig]').forEach(scramble);
            });
        });
        // A direct TAP on the wordmark decodes it too: the one thing that moves it now the field's
        // shockwave no longer floods it. Touch has no hover, so without this it never happened on a
        // phone. It is .hot, so the tap stays out of the field (no ripple, no count toward the game).
        document.querySelectorAll('#layout-1 .logo.wordmark').forEach(wm => {
            wm.addEventListener('click', () => {
                if (deskLayout.matches) return;                  // phones keep the tap decode; desks do not
                wm.querySelectorAll('[data-orig]').forEach(scramble);
            });
        });

        // ---------- menu-hover fx bindings ----------
        // the hover effect (field crossfade + camera personality) holds across
        // the gaps between items — it releases when the pointer leaves the nav
        document.querySelectorAll('.menu').forEach(menu => {
            const items = [...menu.querySelectorAll('.menu-item')];
            const preview = (item, i) => {
                // moving between items restarts the pan-out only when the
                // depth actually changes, so each new item eases out afresh
                if (hoverMenuIdx !== i) hoverStartMs = performance.now();
                hoverMenuIdx = i;
                hoverItemEl = item;
            };
            items.forEach((item, i) => {
                /* THE CART IS NOT A PLACE IN THE FIELD. Every other item summons its own field
                   mood and pulls the camera toward itself on press — and the bag isn't a page you
                   travel to, it's a window that lifts off this one, so that left the mesh swimming
                   behind it while you were reading your bag. Matched on the label rather than the
                   destination list, which is declared further down this file. */
                if (/cart/i.test(item.textContent || '')) return;
                item.addEventListener('mouseenter', () => preview(item, i));
                // touch has no hover — pressing an item summons its field +
                // camera personality; releasing (below) lets it linger a beat
                item.addEventListener('pointerdown', (e) => {
                    if (e.pointerType === 'touch') preview(item, i);
                });
            });
            menu.addEventListener('mouseleave', () => {
                hoverMenuIdx = -1;
                hoverItemEl = null;
            });
        });
        // on touch, release clears the preview after a short beat — unless a
        // page transition has already taken over (then it rides into the entry)
        window.addEventListener('pointerup', (e) => {
            if (e.pointerType !== 'touch') return;
            const idx = hoverMenuIdx;
            setTimeout(() => {
                if (hoverMenuIdx === idx && shopPhase === 'home') { hoverMenuIdx = -1; hoverItemEl = null; }
            }, 450);
        });

        // ---------- shop transition state ----------
        // home → (nav click) → entering: background inverts, UI fades, dots
        // migrate into the card outlines → shop: DOM cards fade in over their
        // particle scaffolds. leaving runs the whole thing in reverse.
        const shopEl = document.getElementById('shop');
        let destOverlay = shopEl; // which page the dots are composing toward
        let shopPhase = 'home';   // home | entering | shop | leaving
        let shopT = 0;
        let menuPull = 0;         // hover camera pull-back (world units)
        let menuSway = 0;         // hover sway strength (0..1)
        let menuPanX = 0, menuPanY = 0;   // camera pan toward the hovered item
        let hoverStartMs = 0;     // when the current menu hover began (drives pan-out)
        let invertCX = window.innerWidth / 2, invertCY = window.innerHeight / 2;
        const invertCenterV = new THREE.Vector2(0, 0);

        // the ripple inversion grows from wherever the triggering click landed
        function setInvertCenter(e) {
            invertCX = e && e.clientX !== undefined ? e.clientX : window.innerWidth / 2;
            invertCY = e && e.clientY !== undefined ? e.clientY : window.innerHeight / 2;
            toWorld(invertCX, invertCY, invertCenterV);
            material.uniforms.uInvertCenter.value.copy(invertCenterV);
        }

        const PAGES = {
            shop:    { dest: 0, el: shopEl },
            archive: { dest: 2, el: document.getElementById('archive') },
            about:   { dest: 3, el: document.getElementById('about') }
        };

        // ---------- infinite product lattice ----------
        // real Shopify products are injected as window.INNO_PRODUCTS by the theme;
        // the fallback keeps the standalone prototype working with no store data
        const PRODUCTS = (window.INNO_PRODUCTS && window.INNO_PRODUCTS.length) ? window.INNO_PRODUCTS : [
            { t: 'Raf Simons AW03 “Closer” Bomber', p: '$540.00 CAD', url: '#', img: '' },
            { t: 'Margiela GAT White Leather', p: '$180.00 CAD', url: '#', img: '' },
            { t: 'CDG Homme Plus Raw Hem Blazer', p: '$340.00 CAD', url: '#', img: '' },
            { t: 'Yohji Pour Homme Wide Trouser', p: '$290.00 CAD', url: '#', img: '' },
            { t: 'Helmut Lang Painter Jean', p: '$420.00 CAD', url: '#', img: '' },
            { t: 'Number (N)ine Cowboy Cardigan', p: '$780.00 CAD', url: '#', img: '' },
            { t: 'Undercover Scab Era Shirt', p: '$650.00 CAD', url: '#', img: '' },
            { t: 'Issey Miyake Pleats Trouser', p: '$260.00 CAD', url: '#', img: '' },
            { t: 'Junya Watanabe Tech Parka', p: '$390.00 CAD', url: '#', img: '' },
            { t: 'Prada Sport Vest 1999', p: '$310.00 CAD', url: '#', img: '' }
        ];
        const pgridEl = document.querySelector('#shop .pgrid');
        let cellCount = 0;
        /* THE CARD PHOTOGRAPH AT THE SIZE THIS SCREEN ACTUALLY NEEDS.

           INNO_PRODUCTS carries one width, 500, because it is inlined into the page and a second
           set of URLs for 125 pieces would cost more bytes than the sharper pictures save. But
           500 is a phone's number: desktop cards are 378px at 1512 and 480px at 1920, so on any
           2× display that is a 1.5–1.9× upscale, and the grid is where the clothes are actually
           sold. The CDN takes the width as a query parameter, so the larger file can simply be
           asked for — no extra markup, no second URL shipped, and only the one chosen is ever
           fetched. A phone's three columns and a 1× desktop still get exactly the 500 they got
           before. */
        const CARD_W = (function () {
            const cols = window.innerWidth >= 990 ? 4 : 3;
            const need = (window.innerWidth / cols) * (window.devicePixelRatio || 1);
            return need > 560 ? 900 : 500;
        })();
        function cardSrc(u) {
            return (CARD_W === 500 || !u) ? u : u.replace(/([?&]width=)\d+/, '$1' + CARD_W);
        }
        // full image list for a piece (all shots, featured first as a fallback)
        function cellImgs(p) {
            const a = (p.imgs && p.imgs.length) ? p.imgs.slice() : [];
            if (!a.length && p.img) a.push(p.img);
            return a.map(cardSrc);
        }
        function esc(s) {
            return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
        }
        // drop the vendor from the shown title when it's duplicated there (the vendor
        // already has its own line); case-insensitive, first match, then tidy separators
        function displayTitle(p) {
            let t = p.t || '';
            const v = (p.v || '').trim();
            if (v) {
                const i = t.toLowerCase().indexOf(v.toLowerCase());
                if (i !== -1) {
                    let s = t.slice(0, i) + t.slice(i + v.length);
                    s = s.replace(/\s{2,}/g, ' ').replace(/^[\s\-–—:·|]+/, '').replace(/[\s\-–—:·|]+$/, '').trim();
                    if (s) t = s;   // never blank the title entirely
                }
            }
            return t;
        }
        function appendCells(n) {
            const start = cellCount;
            const batch = [];
            let html = '';
            // column count → clean TOP-TO-BOTTOM cascade (each row lands together,
            // top row first), instead of a per-cell left-to-right diagonal.
            let cols = getComputedStyle(pgridEl).gridTemplateColumns.split(' ').filter(Boolean).length;
            if (cols < 2) cols = window.innerWidth >= 990 ? 4 : 3;
            // real catalog: each piece appears once (no repeat-to-infinity)
            for (let i = 0; i < n && cellCount < PRODUCTS.length; i++, cellCount++) {
                const p = PRODUCTS[cellCount];
                const imgs = cellImgs(p);
                batch.push(imgs);
                const multi = imgs.length > 1;
                // data-inno-src, not src: inno-imgload owns the transfer so it can report a real
                // percentage. It sets src once the bytes are in hand, so the tag fetches nothing.
                // the lattice is nothing but photographs — an empty alt left the whole shop silent
                const img = imgs.length ? '<img class="p-img" decoding="async" data-inno-src="' + imgs[0] + '" alt="' + esc(p.t) + '">' : '';
                const vend = p.v ? '<span class="p-v">' + esc(p.v) + '</span>' : '';
                // row-by-row fade-in: every card in a row lands together, top row first.
                // slow, obvious per-row stagger so the cascade reads clearly on landing
                const delay = Math.min(Math.floor(i / cols), 24) * 220;
                // hover cycler: +/- on each side + a hairline "photo x of n" bar
                let prog = '', cyc = '';
                if (multi) {
                    let segs = '';
                    for (let s = 0; s < imgs.length; s++) segs += '<i></i>';
                    prog = '<span class="p-prog" style="--n:' + imgs.length + '">' + segs + '<b class="p-prog-thumb"></b></span>';
                    cyc = '<span class="p-cyc p-prev" aria-hidden="true">−</span>'
                        + '<span class="p-cyc p-next" aria-hidden="true">+</span>';
                }
                // sold pieces never reveal the price they went for, but the slot still carries the
                // STATUS — an empty line just read as missing data
                const sold = p.a === false;
                const priceHtml = sold ? '<span class="p-p p-status">Sold</span>'
                                       : '<span class="p-p">' + esc(p.p || '') + '</span>';
                // photo + cycler live in .p-media; the caption strip sits BELOW it
                // data-full-title is the product's REAL title (vendor included). The caption
                // shows displayTitle(), which strips the vendor — so the loader was decoding a
                // different string from the one the product page then puts on screen, and the
                // handoff swapped text at the exact moment it was meant to line up.
                /* QUICK ACTIONS. A tap on a card opens these rather than navigating: VIEW carries
                   on to the piece, ADD puts it straight in the bag. They are <span role="button">
                   and not <button> because the cell is an <a> and a button inside a link is
                   invalid content — and tabindex="-1" because a focusable control inside a link
                   is a mess to operate by keyboard. Keyboard and screen-reader users get the
                   link's own behaviour: activate the card, land on the product page. */
                const canQuick = !sold && p.vid;
                const quick = '<span class="pc-quick" aria-hidden="true">'
                      + '<span class="pc-q" role="button" tabindex="-1" data-pc-view>View</span>'
                      + (canQuick
                          ? '<span class="pc-q pc-q-add" role="button" tabindex="-1" data-pc-add>Add</span>'
                          : '<span class="pc-q is-off" role="button" tabindex="-1">' + (sold ? 'Sold' : 'View only') + '</span>')
                      + '</span>';
                html += '<a class="pcell hot' + (multi ? ' has-cyc' : '') + (sold ? ' is-sold' : '') + '" style="--d:' + delay + 'ms" data-full-title="' + esc(p.t || '') + '"'
                      + ' data-vid="' + esc(String(p.vid == null ? '' : p.vid)) + '"'
                      + ' data-pid="' + esc(String(p.pid == null ? '' : p.pid)) + '"'
                      + ' href="' + (p.url || '#') + '">'
                      + '<span class="p-media">' + img + prog + cyc + '</span>'
                      + '<div class="p-info">' + vend
                      + '<span class="p-t">' + esc(displayTitle(p)) + '</span>'
                      + priceHtml + '</div>' + quick + '</a>';
            }
            pgridEl.insertAdjacentHTML('beforeend', html);
            // hold every fresh cell until it reaches the viewport (see revealInView). The very
            // first screenful is swept synchronously below, so nothing above the fold waits.
            for (let k = start; k < pgridEl.children.length; k++) pgridEl.children[k].classList.add('pc-out');
            // attach each piece's image list to its cell (no attribute bloat)
            const cells = pgridEl.querySelectorAll('.pcell');
            for (let j = 0; j < batch.length; j++) {
                const cell = cells[start + j];
                if (!cell) continue;
                cell._imgs = batch[j];
                cell._idx = 0;
                /* WHAT THE CARD SAYS, TAKEN ONCE. The filter used to read the caption's live text,
                   and the caption is the thing the hover decode scrambles: a card still mid-decode
                   when a search ran was tested as random characters and dropped from the results.
                   Read here, before anything can have animated it, and kept. */
                cell._hay = (((cell.querySelector('.p-v') || {}).textContent || '') + ' ' +
                             ((cell.querySelector('.p-t') || {}).textContent || '')).toLowerCase();
                /* The price comes from the PIECE, not its label. A sold card's label reads "Sold",
                   which parsed as 0 — so every sold piece, a $1,300 coat included, was filed under
                   "Under $300": 52 of the 97 cards that filter showed. The label is what the card
                   says; the price is what the piece costs, and that is what a price filter asks. */
                const src = PRODUCTS[start + j];
                const priceText = (src && src.p) || (cell.querySelector('.p-p') || {}).textContent || '';
                cell._price = parseFloat(String(priceText).replace(/[^0-9.]/g, '') || '0');
                // the lazy <img> resolves long after the cell animates in — fade it on decode
                // so it arrives WITH the caption instead of snapping in behind it
                const pim = cell.querySelector('.p-img');
                if (pim) {
                    if (pim.complete && pim.naturalWidth) pim.classList.add('is-loaded');
                    else pim.addEventListener('load', () => pim.classList.add('is-loaded'), { once: true });
                    // a broken/blocked image must not leave a permanently blank tile
                    pim.addEventListener('error', () => pim.classList.add('is-loaded'), { once: true });
                }
                computeCellLum(cell);   // vertical luminance profile → smart header contrast
                // text-scramble decode of the caption (vendor / title / price) on hover
                const cInfo = cell.querySelector('.p-info');
                if (cInfo) {
                    wrapScrambleTargets(cInfo);
                    cell.addEventListener('mouseenter', () => cInfo.querySelectorAll('[data-orig]').forEach(scramble));
                }
                // hovering the card advances to the next photo; leaving it snaps
                // back to the first (cover) shot
                if (batch[j].length > 1) {
                    // hover-cycling is DESKTOP only — on touch these fire as synthesized events on tap
                    // and fought the swipe (the photo jumped, then snapped back)
                    if (canHover) {
                        cell.addEventListener('mouseenter', () => cycleCell(cell, 1));
                        cell.addEventListener('mouseleave', () => setCellPhoto(cell, 0));
                    }
                    // touch: horizontal drag steps through the photos CONTINUOUSLY (a long swipe can
                    // advance several), and any swipe cancels the tap so it never opens the product
                    const STEP = 46;                 // px of travel per photo
                    let sx0 = 0, sy0 = 0, stepped = 0, swiped = false, locked = null;
                    cell.addEventListener('touchstart', (ev) => {
                        const t = ev.touches[0];
                        sx0 = t.clientX; sy0 = t.clientY; stepped = 0; swiped = false; locked = null;
                        // clear any transform left over from an interrupted gesture — coming back
                        // to the lattice from a product page could restore a card mid-drag, and
                        // the next swipe then started from an offset and fought the finger
                        clearTransform();   // drop anything a previous build or gesture left behind
                    }, { passive: true });
                    // The photo TRACKS THE FINGER and eases home, rather than snapping to the
                    // next frame the instant a threshold is crossed. Between steps it slides a
                    // fraction of the drag (resistance), so the gesture has a physical feel, and
                    // on release it settles on an ease rather than stopping where you let go.
                    // A PLAIN SWIPE. The photo no longer follows the finger, which means it no
                    // longer needs to be drawn oversize to stop its own edge walking into frame —
                    // the drag-follow was the only reason that scale existed. Swiping now simply
                    // steps between photos; nothing is transformed at all.
                    const img0 = cell.querySelector('.p-img');
                    const clearTransform = () => {
                        if (!img0) return;
                        img0.style.transition = '';
                        img0.style.transform = '';
                        img0.style.willChange = '';
                    };
                    cell.addEventListener('touchmove', (ev) => {
                        const t = ev.touches[0], dx = t.clientX - sx0, dy = t.clientY - sy0;
                        // decide once per gesture whether this is a horizontal (photo) or vertical (scroll) drag
                        if (locked === null) {
                            if (Math.abs(dx) < 10 && Math.abs(dy) < 10) return;
                            locked = Math.abs(dx) > Math.abs(dy) * 1.2 ? 'x' : 'y';
                        }
                        if (locked !== 'x') return;
                        // `dx` stays ABSOLUTE for the whole gesture and `stepped` counts against
                        // it. There used to be a re-anchor here that reset the origin after each
                        // step — which left dx near zero while `stepped` still held the old count,
                        // so the very next move saw a mismatch and stepped BACK. Forward, back,
                        // forward: that oscillation was the glitch. It also contradicted the drag
                        // line below, which reads the residual off the same absolute dx.
                        const want = Math.trunc(dx / STEP);          // how many photos this drag has covered
                        if (want !== stepped) {
                            // a fast flick can cross several steps between two touchmove events —
                            // advance the whole difference so the strip keeps up with the finger
                            const delta = want - stepped;
                            const dir = delta < 0 ? 1 : -1;           // drag left → next photo
                            for (let n = Math.abs(delta); n > 0; n--) cycleCell(cell, dir);
                            stepped = want; swiped = true;
                        }
                    }, { passive: true });
                    // a swipe must not navigate: kill the tap on the way out AND on the click
                    cell.addEventListener('touchend', (ev) => {
                        if (swiped && ev.cancelable) ev.preventDefault();
                    }, { passive: false });
                    cell.addEventListener('click', (ev) => { if (swiped) { ev.preventDefault(); ev.stopPropagation(); swiped = false; } });
                }
            }
        }
        // show a specific photo (wraps); updates the image + the hairline bar
        function setCellPhoto(cell, idx, dir) {
            const imgs = cell && cell._imgs;
            if (!imgs || !imgs.length) return;
            const prev = cell._idx || 0;
            cell._idx = ((idx % imgs.length) + imgs.length) % imgs.length;
            const im = cell.querySelector('.p-img');
            const url = imgs[cell._idx];
            if (im && url) {
                // No per-step animation, AND no swap to an un-decoded URL — assigning one blanks
                // the element until the bytes land, which mid-swipe reads as the card snapping
                // back to its cover shot. Decode first, then swap; the token stops a slow image
                // from arriving late and yanking the card backwards after a fast swipe.
                const token = (cell._swapToken = (cell._swapToken || 0) + 1);
                const pre = new Image();
                const swap = () => { if (cell._swapToken === token) im.src = url; };
                pre.src = url;
                /* DECODED, NOT MERELY LOADED — the first-hover flash. `load` means the bytes have
                   arrived; the picture is only turned into pixels when something paints it. And
                   the card's <img> is decoding="async", which lets the browser present a frame
                   WITHOUT the picture while it decodes. So the first time a card was hovered, the
                   swap landed on a URL whose pixels did not exist yet and the card showed its
                   empty ground for a frame or two; every hover after that hit the decoded copy
                   and was clean. decode() does the work off-screen and resolves only when the
                   pixels are ready, so the current photo holds until the next one can replace it
                   outright. A reject (Safari refuses some very large images) falls back to the
                   old load test rather than never swapping at all.
                   Every device: on a phone this same function steps a swipe, and a swipe onto an
                   undecoded photo had the same blank frame. Only the last step of a fast swipe
                   ever lands (the token), so waiting for its decode costs a few ms, not a stall. */
                if (pre.decode) {
                    pre.decode().then(swap, () => { if (pre.complete && pre.naturalWidth) swap(); else pre.onload = swap; });
                } else {
                    pre.onload = swap;
                    if (pre.complete && pre.naturalWidth) im.src = url;   // cached → immediate
                }
                im.classList.add('is-loaded');   // already visible; never re-fade mid-gesture
            }
            // slide the hairline fill to the current photo's segment
            const thumb = cell.querySelector('.p-prog-thumb');
            if (thumb) thumb.style.transform = 'translateX(calc(' + cell._idx + ' * (100% + 3px)))';
            // re-scan the header contrast off the NEW image — only for the right-most
            // column (the one the vertical nav sits over), so cycling stays cheap
            if (cell._idx !== prev && isRightmostCell(cell)) computeCellLum(cell);
        }
        // step a cell's preview image; wraps both ways (last → first → last)
        function cycleCell(cell, dir) {
            if (cell && cell._imgs && cell._imgs.length > 1) setCellPhoto(cell, cell._idx + dir, dir);
        }
        // the +/- controls live inside the <a>, so cancel the navigation on them
        pgridEl.addEventListener('click', (e) => {
            const btn = e.target.closest('.p-cyc');
            if (!btn) return;
            e.preventDefault();
            e.stopPropagation();
            cycleCell(btn.closest('.pcell'), btn.classList.contains('p-next') ? 1 : -1);
        });
        /* ---------- quick actions on a card ----------
           The lattice is a browsing surface, so a tap opens a choice rather than committing to a
           page load: VIEW carries on to the piece, ADD takes it straight to the bag. One card is
           open at a time, and anything that moves the surface puts it away.

           This is registered on the grid, once, rather than per cell — the lattice pages in more
           cards as you scroll, and a per-cell listener would have to be re-bound on every batch. */
        let armedCell = null;
        function disarmCard() {
            if (!armedCell) return;
            armedCell.classList.remove('pc-armed');
            armedCell = null;
        }
        function armCard(cell) {
            if (armedCell === cell) return;
            disarmCard();
            armedCell = cell;
            cell.classList.add('pc-armed');
        }
        pgridEl.addEventListener('click', (e) => {
            const cell = e.target.closest && e.target.closest('.pcell');
            if (!cell) { disarmCard(); return; }
            // VIEW: let the anchor do exactly what it has always done
            if (e.target.closest('[data-pc-view]')) { disarmCard(); return; }
            const add = e.target.closest('[data-pc-add]');
            if (add) {
                e.preventDefault();
                e.stopPropagation();
                if (add.dataset.busy) return;
                add.dataset.busy = '1';
                const label = add.textContent;
                add.textContent = 'Adding';
                Promise.resolve(
                    window.innoAddToCart
                        ? window.innoAddToCart({ id: cell.dataset.vid, productId: cell.dataset.pid })
                        : { added: false, reason: 'error' }
                ).then((res) => {
                    add.textContent = res.added ? 'Added'
                        : res.reason === 'duplicate' ? 'In bag' : 'Unavailable';
                    setTimeout(() => {
                        add.textContent = label;
                        delete add.dataset.busy;
                        disarmCard();
                    }, 1500);
                });
                return;
            }
            // a bare tap on the card opens the choice instead of navigating; tapping it again closes
            e.preventDefault();
            e.stopPropagation();
            if (armedCell === cell) disarmCard(); else armCard(cell);
        });
        shopEl.addEventListener('scroll', disarmCard, { passive: true });
        /* While a phone scrolls the lattice, the 3D field behind it stops drawing and holds its
           last frame. Behind a screen of photographs it's barely visible, but re-rendering it every
           frame competes with the scroll itself for the phone's GPU. It picks up again the moment
           the scrolling settles. */
        let shopScrollQuietAt = 0;
        const coarsePointer = !!(window.matchMedia && window.matchMedia('(pointer: coarse)').matches);
        if (coarsePointer) shopEl.addEventListener('scroll', () => { shopScrollQuietAt = performance.now() + 180; }, { passive: true });
        /* The back button restores this page from the back/forward cache as a FROZEN frame, and
           tapping VIEW freezes it a moment after disarming — bars still sliding out, caption still
           fading back. So on the way out every card is snapped to rest with transitions off, and
           the resting state is committed before the snapshot is taken. On the way back in,
           anything left over is cleared and transitions are handed back. */
        window.addEventListener('pagehide', () => {
            shopEl.classList.add('pc-still');
            disarmCard();
            void shopEl.offsetWidth;
        });
        window.addEventListener('pageshow', () => {
            disarmCard();
            shopEl.querySelectorAll('.pcell.pc-armed').forEach((c) => c.classList.remove('pc-armed'));
            // a frame for the restored layout to settle before transitions come back on, with a
            // timer behind it because a restored page is not guaranteed a prompt animation frame
            requestAnimationFrame(() => shopEl.classList.remove('pc-still'));
            setTimeout(() => shopEl.classList.remove('pc-still'), 80);
        });
        // a tap anywhere that isn't a card closes it — capture, so a handler that swallows the
        // event on the way up can't leave the choice stuck open
        document.addEventListener('pointerdown', (e) => {
            if (armedCell && !(e.target.closest && e.target.closest('.pcell'))) disarmCard();
        }, true);

        // warm a card's other photos on first hover so +/- swaps are INSTANT (no ~3s download wait)
        pgridEl.addEventListener('pointerover', (e) => {
            const cell = e.target.closest && e.target.closest('.pcell');
            if (!cell || cell._warmed || !cell._imgs || cell._imgs.length < 2) return;
            cell._warmed = true;
            cell._imgs.slice(1).forEach(src => { if (src) { const im = new Image(); im.src = src; } });
        });
        // ---------- smart contrast header ----------
        // Each header item samples the luminance of the product content directly
        // beneath it; when that content is too light for the bone text to read, the
        // item animates to void. Per-image vertical luminance profiles (cover-cropped
        // to the 3:4 cell) are precomputed once, so per-frame we only do cheap lookups.
        const lumCanvas = document.createElement('canvas');
        lumCanvas.width = 14; lumCanvas.height = 20;
        const lumCtx = lumCanvas.getContext('2d', { willReadFrequently: true });
        function smallSrc(url, w) {
            try { const u = new URL(url, location.href); u.searchParams.set('width', w); return u.href; }
            catch (e) { return url; }
        }
        function isRightmostCell(cell) {
            if (!cell || !pgridEl) return false;
            const cr = cell.getBoundingClientRect(), gr = pgridEl.getBoundingClientRect();
            return cr.right >= gr.right - 3;   // last column — the one the vertical nav sits over
        }
        function computeCellLum(cell) {
            const src = cell && cell._imgs && cell._imgs[cell._idx || 0];
            if (!src) { cell._lum = null; return; }
            const probe = new Image();
            probe.crossOrigin = 'anonymous';
            probe.onload = function () {
                try {
                    const tw = lumCanvas.width, th = lumCanvas.height;
                    const iw = probe.naturalWidth || tw, ih = probe.naturalHeight || th;
                    const s = Math.max(tw / iw, th / ih);           // object-fit: cover
                    const dw = iw * s, dh = ih * s;
                    lumCtx.clearRect(0, 0, tw, th);
                    lumCtx.drawImage(probe, (tw - dw) / 2, (th - dh) / 2, dw, dh);
                    const d = lumCtx.getImageData(0, 0, tw, th).data;
                    const g = new Float32Array(tw * th);           // full 2D luminance grid
                    for (let p = 0; p < tw * th; p++) {
                        const i = p * 4, a = d[i + 3] / 255;
                        const l = (0.2126 * d[i] + 0.7152 * d[i + 1] + 0.0722 * d[i + 2]) / 255;
                        g[p] = a * l + (1 - a) * 0.9;              // transparent → bone ground
                    }
                    cell._lum = { w: tw, h: th, g: g };
                    scheduleHeaderSample();
                } catch (e) { cell._lum = null; }                  // tainted / blocked → leave text bone
            };
            probe.onerror = function () { cell._lum = null; };
            probe.src = smallSrc(src, 64);
        }
        function lumFromCell(cell, x, y) {
            const prof = cell._lum, r = cell.getBoundingClientRect();
            if (!prof) return cell.querySelector('.p-img') ? null : 0.06;   // no-image cell = void bg (dark)
            const fx = Math.min(0.999, Math.max(0, (x - r.left) / (r.width || 1)));
            const fy = Math.min(0.999, Math.max(0, (y - r.top) / (r.height || 1)));
            return prof.g[Math.floor(fy * prof.h) * prof.w + Math.floor(fx * prof.w)];
        }
        function luminanceAt(x, y) {
            const stack = document.elementsFromPoint(x, y);
            for (let k = 0; k < stack.length; k++) {
                const node = stack[k];
                if (!node || !node.closest) continue;
                if (node.closest('.page-header') || node.closest('.page-filters') ||
                    node.closest('#search-suggest') || node.closest('#pf-menu') ||
                    node.closest('#designer-modal')) continue;
                const cell = node.closest('.pcell');
                if (cell) return lumFromCell(cell, x, y);
                if (node.id === 'shop' || (node.classList && node.classList.contains('pgrid')) ||
                    node.tagName === 'BODY' || node.tagName === 'HTML') return 0.9;   // bone ground → light
            }
            return 0.9;
        }
        let headerEls = [];
        function refreshHeaderEls() {
            headerEls = [].slice.call(shopEl.querySelectorAll(
                '.ph-char, .pf-label, .pf-count, .pf-designer, .pf-dd-btn, .pf-search-btn, .pf-search-input'));
        }
        function sampleHeader() {
            if (!headerEls.length) refreshHeaderEls();
            for (let k = 0; k < headerEls.length; k++) {
                const el = headerEls[k], r = el.getBoundingClientRect();
                if (r.width < 1 || r.height < 1) continue;         // hidden (e.g. collapsed search)
                const lum = luminanceAt(r.left + r.width / 2, r.top + r.height / 2);
                const inv = el.classList.contains('inv');
                if (lum == null) { if (inv) el.classList.remove('inv'); continue; }
                // opposite of what's behind: light bg → dark text, dark bg → light text.
                // crossover ~0.46 (mid of bone 0.87 / void 0.04); tight gap = accurate, not sticky
                if (!inv && lum > 0.5) el.classList.add('inv');
                else if (inv && lum < 0.42) el.classList.remove('inv');
            }
        }
        let headerSampleReq = 0;
        /* Phones skip the sampling outright. It hit-tests and reads luminance for every character
           of the header on every scroll frame, and on a phone the header is locked bone (the CSS
           overrides .inv) with its filters tucked inside the menu, so none of that work was ever
           shown. It was pure cost on exactly the device where scrolling has to stay smooth. */
        const headerSampleSkip = window.matchMedia ? window.matchMedia('(max-width: 600px)') : null;
        function scheduleHeaderSample() {
            if (headerSampleSkip && headerSampleSkip.matches) return;
            if (headerSampleReq) return;
            headerSampleReq = requestAnimationFrame(function () { headerSampleReq = 0; sampleHeader(); });
        }
        shopEl.addEventListener('scroll', scheduleHeaderSample, { passive: true });
        window.addEventListener('resize', function () { refreshHeaderEls(); scheduleHeaderSample(); });

        // ---------- header idle-fade: when a header control (nav / logo / filters) has
        // sat over a card's title for ~0.5s, fade that card's caption until it clears ----
        // an element only "covers" a card if it's actually VISIBLE. The mobile nav is a collapsed
        // panel (opacity:0) that still reports a full-width rect — without this filter every card
        // under that invisible panel faded its caption away, which is why titles vanished on phones.
        function innoChromeVisible(e) {
            var cs = getComputedStyle(e);
            if (cs.display === 'none' || cs.visibility === 'hidden' || parseFloat(cs.opacity) < 0.05) return false;
            var nav = e.closest && e.closest('.ph-nav');
            if (nav && parseFloat(getComputedStyle(nav).opacity) < 0.05) return false;
            return true;
        }
        function innoHeaderBoxes() {
            return [].slice.call(document.querySelectorAll(
                '#shop .ph-logo, #shop .ph-nav a, #shop .page-filters .pf-select, #shop .page-filters .pf-search'
            )).filter(innoChromeVisible).map(function (e) { return e.getBoundingClientRect(); });
        }
        function innoRectsOverlap(a, b) {
            return !(a.right <= b.left || a.left >= b.right || a.bottom <= b.top || a.top >= b.bottom);
        }
        /* Only the rows that can reach the header are measured. Cards sit in DOM order top to
           bottom, so the scan stops at the first caption that starts below the lowest header
           control; captions faded on an earlier pass are remembered and let go when they clear.
           It used to measure every card in the catalogue twice per scroll event. */
        var behindFaded = [];
        /* The header's controls don't move while you scroll, so their boxes are measured once
           and reused for the burst. Reading them per frame meant a getComputedStyle pass over the
           chrome on every frame — and the class this writes invalidates style, so each read forced
           a fresh recalc. */
        var behindBoxes = null, behindBoxesAt = 0;
        function innoHeaderBoxesCached() {
            var now = performance.now();
            if (!behindBoxes || now - behindBoxesAt > 400) { behindBoxes = innoHeaderBoxes(); behindBoxesAt = now; }
            return behindBoxes;
        }
        function innoScanBehindNav(applyFade) {
            var boxes = innoHeaderBoxesCached();
            var floor = 0;
            for (var b = 0; b < boxes.length; b++) floor = Math.max(floor, boxes[b].bottom);
            var cells = shopEl.querySelectorAll('.pcell'), now = [];
            for (var c = 0; c < cells.length; c++) {
                var cell = cells[c];
                if (cell.style.display === 'none') continue;
                var info = cell.querySelector('.p-info');
                if (!info) continue;
                var r = info.getBoundingClientRect();
                if (r.top > floor) break;                                  // every later row is lower still
                for (var i = 0; i < boxes.length; i++) { if (innoRectsOverlap(r, boxes[i])) { now.push(cell); break; } }
            }
            for (var k = 0; k < behindFaded.length; k++) {
                if (now.indexOf(behindFaded[k]) === -1) behindFaded[k].classList.remove('p-behind-nav');   // scrolled clear → text returns
            }
            if (applyFade) for (var m = 0; m < now.length; m++) now[m].classList.add('p-behind-nav');       // under the header → fade
            behindFaded = applyFade ? now : behindFaded.filter(function (x) { return now.indexOf(x) !== -1; });
        }
        var behindRaf = 0;
        shopEl.addEventListener('scroll', function () {
            // one pass per frame (the old dwell timer was 0ms, so it ran a second full pass per event)
            if (!behindRaf) behindRaf = requestAnimationFrame(function () { behindRaf = 0; innoScanBehindNav(true); });
        }, { passive: true });

        // Reveal-on-approach. A scroll sweep rather than IntersectionObserver on purpose: this
        // file has been bitten repeatedly by observers/rAF that never fire in some contexts, and
        // a cell stuck at opacity 0 is a blank tile. A plain rect test on a scroll we already
        // listen to cannot silently stop working, and the sweep is bounded to pending cells.
        // The reveal used to be one-shot: a card faded in once and stayed on. Scrolling back UP
        // through the lattice therefore showed nothing — every card up there had already spent
        // its entrance. Cards are now RE-ARMED once they're well clear of the viewport, so they
        // fade again whenever they come back, whichever way you're travelling.
        //
        // Two thresholds, not one. Revealing at +120px but only re-arming beyond 260px leaves a
        // dead band between them: without that gap, a card resting near the edge would flip
        // between armed and revealed on the smallest scroll jitter and strobe.
        // Fade BOTH ways. The test is the card's own centre against the viewport edges: while the
        // centre is on screen the card is solid, and it fades as the centre crosses out — so the
        // fade is visible at the edge rather than happening off-screen where nobody sees it.
        // A transition needs no hysteresis: scrubbing the scroll back and forth just reverses the
        // fade mid-flight instead of retriggering it.
        // LEAD, not the card's centre. Testing the centre meant a card arriving from below stayed
        // at zero opacity until it was already half on screen — so its leading half crossed the
        // fold as bare ground. Waking cards a full screen-margin before they arrive means the
        // fade is over by the time any part of one is visible; the cost is that the fade-out now
        // plays just off-screen, which is the right way round: an unseen fade-out beats a visible
        // blank.
        const LEAD = 260;
        function revealInView() {
            const cells = shopEl.querySelectorAll('.pcell');
            if (!cells.length) return;
            const h = shopEl.clientHeight || window.innerHeight;
            // Read every rect BEFORE touching a single class. Interleaving reads and writes
            // makes the browser re-run layout per card; on a hundred-odd cards that is the
            // difference between a smooth scroll and a stuttering one.
            const work = [];
            for (let i = 0; i < cells.length; i++) {
                const c = cells[i];
                if (c.style.display === 'none') continue;
                const r = c.getBoundingClientRect();
                work.push([c, r.bottom < -LEAD || r.top > h + LEAD]);
            }
            for (let i = 0; i < work.length; i++) {
                const c = work[i][0], gone = work[i][1];
                c.classList.toggle('pc-out', gone);
                // Warm EVERY photo the first time a card comes near, not on first touch. Swaps
                // wait for a decode (so a half-loaded image can't flash), which meant the first
                // swipe on a cold card stalled against the network.
                if (!gone && !c._warmed && c._imgs && c._imgs.length > 1) {
                    c._warmed = true;
                    // from index 1 — the cover shot is inno-imgload's job, and warming it here
                    // too would put the same URL on the wire twice
                    c._imgs.slice(1).forEach(src => { if (src) { const im = new Image(); im.src = src; } });
                }
            }
        }
        window.innoRevealInView = revealInView;   // also called right after each append
        /* Bring the whole catalogue into the DOM ahead of any search. Held cards stay at opacity
           0 until they reach the viewport, exactly as the paged ones do, so this changes what is
           BUILT and nothing about what is seen. */
        window.innoFillCatalogue = function () {
            if (cellCount >= PRODUCTS.length) return false;
            appendCells(PRODUCTS.length);
            revealInView();
            return true;
        };
        let revealRaf = 0;
        appendCells(30);
        // deliberately NOT swept here. This runs while the lattice is still a hidden overlay, so
        // every rect reads as on-screen and the first viewport would arrive already at full
        // opacity. Leaving the cards faded until settlePage sweeps them is what gives the first
        // screenful its fade-up instead of simply appearing.
        shopEl.addEventListener('scroll', () => {
            if (shopEl.scrollTop + shopEl.clientHeight > shopEl.scrollHeight - 600) appendCells(15);
            if (!revealRaf) revealRaf = requestAnimationFrame(() => { revealRaf = 0; revealInView(); });
            // rAF is not guaranteed here (backgrounded tab, stalled compositor) — a timer makes
            // sure a pending cell can never be stranded invisible
            clearTimeout(shopEl._revealT);
            shopEl._revealT = setTimeout(revealInView, 90);
        }, { passive: true });

        // ---------- shared page header (logo + nav) + filters ----------
        const DESIGNERS = (window.INNO_VENDORS && window.INNO_VENDORS.length) ? window.INNO_VENDORS
            : ['Raf Simons', 'Margiela', 'CDG', 'Yohji', 'Helmut Lang',
               'Number (N)ine', 'Undercover', 'Issey Miyake', 'Junya Watanabe', 'Prada'];
        const INNO_CART = (typeof window.INNO_CART_COUNT === 'number') ? window.INNO_CART_COUNT : 0;
        const PAGE_NAV = [['shop', 'Shop'], ['archive', 'Visit'], ['about', 'Index'], ['cart', 'Cart (' + INNO_CART + ')']];
        // custom filter dropdowns (replace the basic native <select>s)
        const PRICE_OPTS = [{ v: '', l: 'Any price' }, { v: 'lo', l: 'Under $300' }, { v: 'mid', l: '$300–600' }, { v: 'hi', l: 'Over $600' }];
        const TIME_OPTS = [{ v: 'new', l: 'Newest first' }, { v: 'old', l: 'Oldest first' }];
        const DD_OPTS = { price: PRICE_OPTS, time: TIME_OPTS };
        function buildDropdown(name, opts, cur) {
            const curOpt = opts.find(o => o.v === cur) || opts[0];
            // menu lives in a shared root-level element (see wiring) so the header
            // hairline can't render over it — page overlays trap z-index otherwise
            return `<div class="pf-dd" data-dd="${name}">`
                 + `<button type="button" class="pf-select pf-dd-btn hot" data-dd-toggle aria-haspopup="listbox"><span class="pf-dd-cur">${curOpt.l}</span><span class="pf-dd-caret" aria-hidden="true"></span></button>`
                 + `<input type="hidden" data-filter="${name}" value="${cur}"></div>`;
        }
        const NAV_ICON = {
            shop:    '<svg class="nav-i" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M2 6l1-3h10l1 3M3 6v7h10V6M6 13V9.5h4V13"/></svg>',
            archive: '<svg class="nav-i" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M8 1.6c2.4 0 4.4 2 4.4 4.4C12.4 9.4 8 14.4 8 14.4S3.6 9.4 3.6 6C3.6 3.6 5.6 1.6 8 1.6z"/><circle cx="8" cy="6" r="1.55"/></svg>',
            about:   '<svg class="nav-i" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M5.5 4h8.5M5.5 8h8.5M5.5 12h8.5M2.2 4h.01M2.2 8h.01M2.2 12h.01"/></svg>',
            cart:    '<svg class="nav-i" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><path d="M3 3.4h1.7l1.1 7.2h6.2l1.1-5.4H5.2"/></svg>'
        };
        function buildHeader(withFilters) {
            // label + inline currentColor icon; the icon shows (and the label hides) only on mobile CSS
            const nav = PAGE_NAV.map(([k, l], i) =>
                // `.idx` mirrors the homescreen menu's 01/02/03 numbering — the mobile panel is
                // styled as that same list (see the max-width:600px block), so the two menus read
                // as one piece of design rather than two unrelated navs
                `<a class="hot" data-goto="${k}" data-nav-icon aria-label="${l.replace(/\s*\(.*\)/, '')}">${NAV_ICON[k] || ''}<span class="nav-idx" aria-hidden="true">${String(i + 1).padStart(2, '0')}</span><span class="nav-label">${l}</span></a>`
            ).join('');
            let filters = '';
            if (withFilters) {
                // designer opens a full-screen picker; a hidden input carries its
                // value so the existing filter code reads it unchanged
                filters = `<div class="page-filters">
                    <span class="pf-label">Filter /</span>
                    <button type="button" class="pf-select pf-designer hot" data-designer-open><span class="pf-dz">All designers</span></button>
                    <input type="hidden" data-filter="designer" value="">
                    ${buildDropdown('price', PRICE_OPTS, '')}
                    ${buildDropdown('time', TIME_OPTS, 'new')}
                    <div class="pf-search">
                        <input type="text" class="pf-search-input hot" data-filter="search" placeholder="Search" value="" aria-label="Search pieces">
                        <button type="button" class="pf-select pf-search-btn hot" data-search-toggle aria-label="Search"><svg viewBox="0 0 16 16" width="12" height="12" fill="none" stroke="currentColor" stroke-width="1.5"><circle cx="6.8" cy="6.8" r="4.6"/><line x1="10.2" y1="10.2" x2="14" y2="14"/></svg></button>
                    </div>
                    <span class="pf-count"></span>
                </div>`;
            }
            // mobile-only bar controls. The CART stays permanently visible next to the burger —
            // burying the bag behind a menu is the one thing no storefront does, because it hides
            // the state customers check most (and the route to checkout).
            const cartBtn = `<button type="button" class="ph-cart hot" data-goto="cart" aria-label="Cart">`
                          + `${NAV_ICON.cart}<span class="ph-cart-n" data-cart-count${INNO_CART ? '' : ' hidden'}>${INNO_CART}</span></button>`;
            const burger = `<button type="button" class="ph-burger hot" data-nav-toggle aria-label="Menu" aria-expanded="false"><span></span><span></span><span></span></button>`;
            return `<header class="page-header"><a class="ph-logo hot" data-back>Innocences</a><div class="ph-bar-actions">${cartBtn}${burger}</div><nav class="ph-nav">${nav}</nav></header>${filters}`;
        }
        document.getElementById('shop').insertAdjacentHTML('afterbegin', buildHeader(true));
        document.getElementById('archive').insertAdjacentHTML('afterbegin', buildHeader(false));
        document.getElementById('about').insertAdjacentHTML('afterbegin', buildHeader(false));
        /* INDEX.LOG — each entry's body sits clamped under a fade and READ opens it in place.
           The open height is the body's own scrollHeight, so the ease covers the real distance;
           once it lands the pin is released to `none`, so an image that loads late or a
           rotation can never clip an open entry. The overlay only ever hides by visibility,
           so it keeps its layout and everything here can be measured while it's off screen. */
        (function () {
            const about = document.getElementById('about');
            if (!about || !about.querySelector('.ilog')) return;
            function measure() {
                about.querySelectorAll('.ilog-entry').forEach(function (en) {
                    if (en.classList.contains('is-open')) return;
                    const body = en.querySelector('.ilog-body');
                    if (!body) return;
                    en.classList.remove('is-short');
                    // a body that already fits under the clamp gets no fade and no control
                    en.classList.toggle('is-short', body.scrollHeight <= body.clientHeight + 2);
                });
            }
            measure();
            if (document.fonts && document.fonts.ready) document.fonts.ready.then(measure);
            let rt = 0;
            window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(measure, 200); });
            about.addEventListener('transitionend', function (e) {
                if (e.propertyName !== 'max-height' || !e.target.classList.contains('ilog-body')) return;
                if (e.target.closest('.ilog-entry').classList.contains('is-open')) e.target.style.maxHeight = 'none';
            });
            about.addEventListener('click', function (e) {
                const btn = e.target.closest('.ilog-more');
                if (!btn) return;
                const en = btn.closest('.ilog-entry');
                const body = en.querySelector('.ilog-body');
                const open = !en.classList.contains('is-open');
                const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                if (open) {
                    en.classList.add('is-open');
                    body.style.maxHeight = still ? 'none' : body.scrollHeight + 'px';
                } else {
                    // closing a long entry from its foot would yank the page — bring its head
                    // back under the bar first, then fold it
                    const top = en.getBoundingClientRect().top;
                    if (top < 56) about.scrollTo({ top: about.scrollTop + top - 64, behavior: still ? 'auto' : 'smooth' });
                    body.style.maxHeight = body.scrollHeight + 'px';   // from `none` to a number, so it can ease
                    void body.offsetHeight;
                    body.style.maxHeight = '';
                    en.classList.remove('is-open');
                }
                btn.textContent = open ? 'Close' : 'Read';
                btn.setAttribute('aria-expanded', String(open));
            });
        })();
        /* VISIT — a caret on today's row of the hours. Today in TORONTO, not wherever the
           visitor is: the clock beside it reads YYZ, so the two agree about what day it is. */
        (function () {
            const rows = document.querySelectorAll('#archive .vis-hours [data-days]');
            if (!rows.length) return;
            const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
            function markToday() {
                const d = DAYS.indexOf(new Date().toLocaleDateString('en-US', { timeZone: 'America/Toronto', weekday: 'short' }));
                rows.forEach(function (r) { r.classList.toggle('is-today', r.dataset.days.split(',').map(Number).indexOf(d) !== -1); });
            }
            markToday();
            setInterval(markToday, 60000);
        })();
        /* INDEX + VISIT — text under the bar steps aside.
           The shop's p-behind-nav rule, carried over: whatever text the header's controls are sitting
           on fades out until it scrolls clear, so the wordmark, menu and bag read over a transparent
           bar. The shop fades a card's whole caption, which is two short lines. These pages are
           long-form, where a paragraph vanishing because its first line reached the bar would take
           most of a screen with it, so the unit here is the LINE. Words are wrapped once at load
           and grouped into lines at scan time by where they've wrapped to; a line fades when it
           overlaps any visible header control.
           Readouts that rewrite their own text (the clock, READ/CLOSE) or carry pseudo-element marks
           (brackets, the hours caret) are kept whole rather than split, so the marks fade with them. */
        (function () {
            const WHOLE = '.clock, .ilog-more, .vis-cta, .ilog-eof, .ilog-stamp > span, .vis-lbl, .vis-place, .vis-day, .vis-step-n';
            const BLOCK = 'p, li, dt, dd, h1, h2, h3, h4, h5, h6, blockquote, figcaption, .ilog-stamp, .ilog-foot, .vis-frame';
            function prepare(page) {
                const root = page.querySelector('.ilog');
                if (!root) return null;
                // the `>` before each paragraph was a ::before — a real node fades with its line
                root.querySelectorAll('.ilog-body p').forEach(function (p) {
                    const gt = document.createElement('span');
                    gt.className = 'ilog-gt hf-u';
                    gt.setAttribute('aria-hidden', 'true');
                    gt.textContent = '> ';
                    p.insertBefore(gt, p.firstChild);
                });
                root.classList.add('hf-ready');
                root.querySelectorAll(WHOLE).forEach(function (el) {
                    if (!(el.parentElement && el.parentElement.closest('.hf-u'))) el.classList.add('hf-u');
                });
                const texts = [];
                const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
                    acceptNode: function (n) {
                        if (!n.nodeValue.trim()) return NodeFilter.FILTER_REJECT;
                        if (n.parentElement.closest('.hf-u, script, style')) return NodeFilter.FILTER_REJECT;
                        return NodeFilter.FILTER_ACCEPT;
                    }
                });
                while (walker.nextNode()) texts.push(walker.currentNode);
                texts.forEach(function (n) {
                    const frag = document.createDocumentFragment();
                    n.nodeValue.split(/(\s+)/).forEach(function (part) {
                        if (!part) return;
                        // whitespace stays a text node, so every break opportunity is where it was
                        if (!part.trim()) { frag.appendChild(document.createTextNode(part)); return; }
                        const w = document.createElement('span');
                        w.className = 'hf-w';
                        w.textContent = part;
                        frag.appendChild(w);
                    });
                    n.parentNode.replaceChild(frag, n);
                });
                // one group per text block: a block's rect is read first, and only a block that
                // actually reaches the bar has its words measured
                const groups = new Map();
                root.querySelectorAll('.hf-w, .hf-u').forEach(function (u) {
                    const b = u.parentElement.closest(BLOCK) || u.parentElement;
                    if (!groups.has(b)) groups.set(b, []);
                    groups.get(b).push(u);
                });
                return { page: page, groups: Array.from(groups), faded: new Set() };
            }
            function rectOf(r) { return { left: r.left, right: r.right, top: r.top, bottom: r.bottom }; }
            function scan(st) {
                const boxes = [].slice.call(st.page.querySelectorAll(
                    '.page-header .ph-logo, .page-header .ph-burger, .page-header .ph-cart, .page-header .ph-nav a'
                )).filter(innoChromeVisible).map(function (e) { return e.getBoundingClientRect(); })
                  .filter(function (r) { return r.width > 0 && r.height > 0; });
                const next = new Set();
                if (boxes.length) {
                    let bandTop = Infinity, bandBottom = -Infinity;
                    boxes.forEach(function (r) { bandTop = Math.min(bandTop, r.top); bandBottom = Math.max(bandBottom, r.bottom); });
                    st.groups.forEach(function (g) {
                        const br = g[0].getBoundingClientRect();
                        if (br.bottom <= bandTop || br.top >= bandBottom) return;
                        let line = null;
                        const flush = function () {
                            if (line && boxes.some(function (b) { return innoRectsOverlap(line.r, b); })) {
                                line.us.forEach(function (u) { next.add(u); });
                            }
                        };
                        g[1].forEach(function (u) {
                            const r = u.getBoundingClientRect();
                            if (!r.width && !r.height) return;
                            if (line && Math.abs(r.top - line.r.top) < Math.max(4, r.height * 0.5)) {
                                line.us.push(u);
                                line.r.left = Math.min(line.r.left, r.left);
                                line.r.right = Math.max(line.r.right, r.right);
                                line.r.bottom = Math.max(line.r.bottom, r.bottom);
                            } else {
                                flush();
                                line = { us: [u], r: rectOf(r) };
                            }
                        });
                        flush();
                    });
                }
                st.faded.forEach(function (u) { if (!next.has(u)) u.classList.remove('hf-off'); });
                next.forEach(function (u) { if (!st.faded.has(u)) u.classList.add('hf-off'); });
                st.faded = next;
            }
            const states = {};
            ['about', 'archive'].forEach(function (id) {
                const page = document.getElementById(id);
                const st = page && prepare(page);
                if (!st) return;
                states[id] = st;
                const run = function () { if (document.body.classList.contains(id + '-page')) scan(st); };
                let raf = 0, settle = 0;
                const schedule = function (e) {
                    // a word finishing its own fade is not a reason to look again
                    if (e && e.type === 'transitionend' && e.target.classList &&
                        (e.target.classList.contains('hf-w') || e.target.classList.contains('hf-u'))) return;
                    if (!raf) raf = requestAnimationFrame(function () { raf = 0; run(); });
                    // and once more when things settle — a frame is not promised everywhere
                    clearTimeout(settle);
                    settle = setTimeout(run, 90);
                };
                page.addEventListener('scroll', schedule, { passive: true });
                page.addEventListener('transitionend', schedule);   // an entry opening, the menu closing
                window.addEventListener('resize', schedule);
                // the page settling in, and anything else the body announces
                new MutationObserver(function () { schedule(); }).observe(document.body, { attributes: true, attributeFilter: ['class'] });
            });
            window.innoHeaderFade = function (id) { const st = states[id || 'about']; if (!st) return 'no page'; scan(st); return st.faded.size + ' units faded'; };
        })();
        document.getElementById('shop').classList.add('has-filters');
        // MOBILE: the page carries only the logo + hamburger — the whole filter row lives inside the
        // menu instead. Reparented (not cloned) so every existing `#shop [data-filter]` query and all
        // the wiring keeps working untouched.
        (function () {
            const mq = window.matchMedia('(max-width: 600px)');
            function placeFilters() {
                const shop = document.getElementById('shop');
                if (!shop) return;
                const filters = shop.querySelector('.page-filters');
                const nav = shop.querySelector('.ph-nav');
                const header = shop.querySelector('.page-header');
                if (!filters || !nav || !header) return;
                if (mq.matches) {
                    if (filters.parentElement !== nav) nav.appendChild(filters);
                } else if (filters.parentElement !== shop) {
                    header.insertAdjacentElement('afterend', filters);   // back to its original slot
                }
            }
            placeFilters();
            (mq.addEventListener ? mq.addEventListener('change', placeFilters) : mq.addListener(placeFilters));
        })();
        // ---- mobile bar behaviour: live cart badge, scroll-lock behind the menu, auto-hiding header ----
        (function () {
            const mqMobile = window.matchMedia('(max-width: 600px)');

            // the badge tracks the real cart, so the bag count is right without a reload
            function paintCart(n) {
                document.querySelectorAll('[data-cart-count]').forEach(function (el) {
                    el.textContent = n;
                    if (n > 0) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
                });
            }
            document.addEventListener('inno:cart:updated', function (e) {
                const c = e && e.detail; if (c && typeof c.item_count === 'number') paintCart(c.item_count);
            });
            if (window.fetch) {
                fetch('/cart.js', { headers: { Accept: 'application/json' } })
                    .then(function (r) { return r.json(); })
                    .then(function (c) { if (c && typeof c.item_count === 'number') paintCart(c.item_count); })
                    .catch(function () {});
            }

            // with the menu open the lattice behind it must not scroll (it dragged under the panel
            // and lost your place); #shop is the scroll container, so freeze IT, not the body
            const shopEl2 = document.getElementById('shop');
            let lockedAt = 0;
            window.innoLockScroll = function (on) {
                if (!shopEl2) return;
                if (on) { lockedAt = shopEl2.scrollTop; shopEl2.style.overflowY = 'hidden'; }
                else { shopEl2.style.overflowY = ''; shopEl2.scrollTop = lockedAt; }
            };

        })();

        // mobile hamburger: toggle the nav panel; any nav tap or an outside tap closes it
        document.addEventListener('click', function (e) {
            const btn = e.target.closest && e.target.closest('[data-nav-toggle]');
            if (btn) {
                const hd = btn.closest('.page-header');
                const open = hd.classList.toggle('nav-open');
                btn.setAttribute('aria-expanded', open ? 'true' : 'false');
                if (window.innoLockScroll) window.innoLockScroll(open);
                if (open) innoMenuDecode(hd); else innoMenuClear(hd);
                return;
            }
            document.querySelectorAll('.page-header.nav-open').forEach(function (hd) {
                /* Only a tap inside the PANEL keeps it open. This used to test the whole header,
                   which also contains the BAR — the wordmark, the cart — and those sit above the
                   panel and are meant to be usable while it is open. So tapping the wordmark to
                   go home left nav-open set on a header whose overlay then hid, and the panel is
                   a full-viewport fixed sheet: .page-ov drops to pointer-events:none but a
                   descendant that sets `auto` beats an ancestor's `none`, so it stayed over the
                   homescreen, invisible, swallowing every tap. */
                if (e.target.closest('.ph-nav') && !e.target.closest('.ph-nav a')) return;
                hd.classList.remove('nav-open');
                const b = hd.querySelector('[data-nav-toggle]'); if (b) b.setAttribute('aria-expanded', 'false');
                if (window.innoLockScroll) window.innoLockScroll(false);
            });
        });
        // split the shop header logo + nav into per-character spans so the smart
        // contrast can invert each character against the content beneath IT
        document.querySelectorAll('#shop .ph-logo, #shop .ph-nav a').forEach(function (el) {
            // icon nav links carry an <svg> + a .nav-label — split the label so the SVG survives
            const target = el.querySelector('.nav-label') || el;
            const txt = target.textContent;
            target.textContent = '';
            for (let i = 0; i < txt.length; i++) {
                const sp = document.createElement('span');
                sp.className = 'ph-char';
                sp.textContent = txt[i];
                target.appendChild(sp);
            }
        });
        // The slide-out menu resolves like the BIOS intro: each label decodes out of a
        // character scramble instead of just fading up. Modernised in two ways — it runs on
        // the per-character spans (so it stays crisp rather than reflowing the line) and it
        // cascades top-to-bottom in step with the panel's own staggered reveal, rather than
        // the boot sequence's strict line-by-line wait.
        function innoEnsureChars(el) {
            const target = el.querySelector('.nav-label') || el;
            if (target.querySelector('.ph-char')) return;   // #shop's nav is already split
            const txt = target.textContent;
            target.textContent = '';
            for (let i = 0; i < txt.length; i++) {
                const sp = document.createElement('span');
                sp.className = 'ph-char';
                sp.textContent = txt[i];
                target.appendChild(sp);
            }
        }
        // plain-text decode for menu content that isn't split into .ph-char spans (the filter
        // row's labels, the designer button, the two dropdown values, the item count)
        function innoDecodeText(el) {
            const text = (el._decOrig != null) ? el._decOrig : (el._decOrig = el.textContent);
            if (!text.trim()) return;
            if (el._decRaf) cancelAnimationFrame(el._decRaf);
            const start = performance.now(), dur = 360 + text.length * 16;
            clearTimeout(el._decFail);
            el._decFail = setTimeout(() => {                    // rAF may never run — never leave garbage
                if (el._decRaf) { cancelAnimationFrame(el._decRaf); el._decRaf = null; }
                el.textContent = text;
            }, 1400);
            const step = (now) => {
                const p = (now - start) / dur;
                if (p >= 1) { el.textContent = text; el._decRaf = null; return; }
                let out = '';
                for (let i = 0; i < text.length; i++) {
                    const c = text[i];
                    out += (c === ' ' || p * text.length * 1.6 > i) ? c
                         : SCR_CHARS[(Math.random() * SCR_CHARS.length) | 0];
                }
                el.textContent = out;
                el._decRaf = requestAnimationFrame(step);
            };
            el._decRaf = requestAnimationFrame(step);
        }
        function innoMenuDecode(hd) {
            // only the mobile panel does this — on desktop the nav is inline chrome, not a menu
            if (!window.matchMedia || !window.matchMedia('(max-width: 600px)').matches) return;
            const nav = hd.querySelector('.ph-nav');
            if (!nav) return;
            // The real cascade runs over LINES, not over .ph-nav's children. The filter row is a
            // single child holding five lines, so stepping by child made those five land together
            // and then write in over the following half second — the "second set is slower" you
            // could see. Flattening it means every line, wherever it lives, gets the same step.
            const lines = [];
            // Anything with no box is skipped — the filter row carries hidden <input>s that hold
            // its state, and letting one take a slot spends 130ms of the cascade on nothing
            // visible, which reads as a stutter partway down the menu.
            const visible = function (e) { return e.getClientRects().length > 0; };
            [].slice.call(nav.children).forEach(function (child) {
                if (child.classList.contains('page-filters')) {
                    [].slice.call(child.children).filter(visible).forEach(function (g) { lines.push(g); });
                } else if (visible(child)) lines.push(child);
            });
            lines.forEach((owner, i) => {
                const at = 340 + i * 130;
                // the fade and the write-in are driven from the same number, so they cannot drift
                owner.style.transitionDelay = (at / 1000) + 's';
                let target = null;
                if (owner.tagName === 'A') target = owner;
                else if (owner.matches('.pf-label, .pf-designer, .pf-count')) target = owner;
                else target = owner.querySelector('.pf-dd-cur, .p-track, .menu-ig-t');
                if (!target) return;                       // e.g. the search icon — nothing to write
                clearTimeout(target._decodeT);
                target._decodeT = setTimeout(() => {
                    if (target.tagName === 'A') { innoEnsureChars(target); scrambleChars(target); }
                    else innoDecodeText(target);
                }, at);
            });
        }
        // closing hands the reverse cascade back to CSS, which carries !important on the closed
        // delays — leaving the open values inline would fight it
        function innoMenuClear(hd) {
            const nav = hd.querySelector('.ph-nav');
            if (!nav) return;
            nav.querySelectorAll('[style*="transition-delay"]').forEach(function (el) {
                el.style.transitionDelay = '';
            });
        }

        // text-scramble decode on hover for the shop nav + logo (works over the char spans)
        function scrambleChars(el) {
            const chars = [].slice.call(el.querySelectorAll('.ph-char'));
            if (!chars.length) return;
            const origs = chars.map(c => (c._orig != null ? c._orig : (c._orig = c.textContent)));
            if (el._scrRaf) cancelAnimationFrame(el._scrRaf);
            // Hard stop. The decode is rAF-driven, and a stalled frame loop would leave a menu
            // label as permanent garbage — which for the hover effect was cosmetic but for the
            // menu means unreadable navigation. This restores the real text no matter what.
            clearTimeout(el._scrFail);
            el._scrFail = setTimeout(() => {
                if (el._scrRaf) { cancelAnimationFrame(el._scrRaf); el._scrRaf = null; }
                chars.forEach((c, i) => { c.textContent = origs[i]; });
            }, 1400);
            const start = performance.now(), dur = 340 + chars.length * 18;
            (function step(now) {
                const p = (now - start) / dur;
                if (p >= 1) { chars.forEach((c, i) => c.textContent = origs[i]); el._scrRaf = null; return; }
                for (let i = 0; i < chars.length; i++) {
                    const o = origs[i];
                    chars[i].textContent = (o === ' ' || p * chars.length * 1.6 > i) ? o : SCR_CHARS[(Math.random() * SCR_CHARS.length) | 0];
                }
                el._scrRaf = requestAnimationFrame(step);
            })(start);
        }
        document.querySelectorAll('#shop .ph-logo, #shop .ph-nav a').forEach(function (el) {
            el.addEventListener('mouseenter', () => scrambleChars(el));
        });
        // scramble the filter controls on hover too (re-wrap each time so it catches
        // the designer / price / sort labels after they change on selection)
        document.querySelectorAll('#shop .page-filters .pf-select, #shop .page-filters .pf-label').forEach(function (el) {
            el.addEventListener('mouseenter', () => { wrapScrambleTargets(el); el.querySelectorAll('[data-orig]').forEach(scramble); });
        });

        // shop filter: designer + price show/hide, time reverses order, live count
        // FLIP: measure where the visible cards are, let `mutate` change the grid, then invert the
        // difference and play it out — so cards slide into the gaps rather than snapping to them.
        function flipGrid(shop, mutate) {
            const grid = shop.querySelector('.pgrid');
            if (!grid) { mutate(); return; }
            const before = new Map();
            grid.querySelectorAll('.pcell').forEach(c => {
                if (c.style.display === 'none') return;
                before.set(c, c.getBoundingClientRect());
            });
            mutate();
            grid.querySelectorAll('.pcell').forEach(c => {
                if (c.style.display === 'none') return;
                const f = before.get(c);
                if (!f) return;                                  // just revealed → its own reveal animation
                const l = c.getBoundingClientRect();
                const dx = f.left - l.left, dy = f.top - l.top;
                if (!dx && !dy) return;
                c.style.transition = 'none';
                c.style.transform = 'translate(' + dx + 'px,' + dy + 'px)';
                // reduced-motion shortens the glide rather than removing it — snapping back to
                // an instant jump is the very thing this replaced
                const quick = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                const dur = quick ? 200 : 520;
                // The play step is driven by rAF, but NEVER only by it: if rAF is throttled or
                // suspended the card would be left sitting at its inverted offset. A timer fallback
                // plays it, and a final sweep strips the inline styles no matter what happened.
                let played = false;
                const play = () => {
                    if (played) return;
                    played = true;
                    c.style.transition = 'transform ' + dur + 'ms cubic-bezier(.22,1,.36,1)';
                    c.style.transform = '';
                };
                requestAnimationFrame(play);
                clearTimeout(c._flipPlay); c._flipPlay = setTimeout(play, 60);
                clearTimeout(c._flipEnd);
                c._flipEnd = setTimeout(() => { c.style.transition = ''; c.style.transform = ''; }, dur + 140);
            });
        }

        function shopFilterState() {
            const shop = document.getElementById('shop');
            return {
                dz: shop.querySelector('[data-filter="designer"]').value,
                pz: shop.querySelector('[data-filter="price"]').value,
                tz: shop.querySelector('[data-filter="time"]').value,
                sq: ((shop.querySelector('[data-filter="search"]') || {}).value || '').trim().toLowerCase()
            };
        }
        // match designer + search against vendor AND title (the vendor is stripped from the
        // visible title, so title alone would miss pieces) — from what the card was built saying
        function cellMatches(cell, f) {
            const hay = cell._hay != null ? cell._hay
                : (((cell.querySelector('.p-v') || {}).textContent || '') + ' ' + ((cell.querySelector('.p-t') || {}).textContent || '')).toLowerCase();
            const price = cell._price != null ? cell._price
                : parseFloat(((cell.querySelector('.p-p') || {}).textContent || '').replace(/[^0-9.]/g, '') || '0');
            if (f.dz && !hay.includes(f.dz.toLowerCase())) return false;
            if (f.sq && !hay.includes(f.sq)) return false;
            if (f.pz === 'lo' && price >= 300) return false;
            if (f.pz === 'mid' && (price < 300 || price > 600)) return false;
            if (f.pz === 'hi' && price <= 600) return false;
            return true;
        }
        function applyShopFilter() {
            const shop = document.getElementById('shop');
            const f = shopFilterState();
            const tz = f.tz;
            // a filter/search must see the whole catalogue, not just the lazy page
            const hasFilter = !!(f.dz || f.pz || f.sq);
            if (hasFilter && cellCount < PRODUCTS.length) appendCells(PRODUCTS.length);
            let shown = 0;
            const revealed = [];   // cells that just became visible → blur-fade them back in
            shop.querySelectorAll('.pcell').forEach(cell => {
                if (!cell.querySelector('.p-t')) return;
                const ok = cellMatches(cell, f);
                const isHidden = cell.style.display === 'none';
                if (ok) {
                    shown++;
                    clearTimeout(cell._hideT);
                    cell.classList.remove('pc-hide');                 // cancel any in-flight fade-out
                    /* Every match arrives TOGETHER while a filter is on, rather than trickling in
                       as the viewport sweep catches up — which is what made searching feel choppy:
                       the one match already on the lazy page appeared at once and the rest waited.
                       A rect-based sweep cannot do this job here, because flipGrid parks the
                       survivors at their pre-collapse positions to glide them, so they still read
                       as off-screen. Releasing them costs no bandwidth: the photographs load on
                       proximity regardless of opacity. Unfiltered, the sweep still holds the
                       catalogue back as before. */
                    if (hasFilter) cell.classList.remove('pc-out');
                    if (isHidden) { cell.classList.remove('pc-reveal'); revealed.push(cell); }
                } else if (!isHidden && !cell.classList.contains('pc-hide')) {
                    cell.classList.add('pc-hide');                    // no longer matches → fade OUT
                }
            });
            // Reveals are applied inside a FLIP so the cards already on screen GLIDE to their new
            // slots instead of teleporting when the grid reflows around them.
            if (revealed.length) {
                flipGrid(shop, () => { revealed.forEach(c => { c.style.display = ''; }); });
                revealed.forEach((c, i) => {
                    c.style.setProperty('--rd', Math.min(i * 45, 520) + 'ms');   // capped so long lists don't crawl
                    c.classList.add('pc-reveal');
                });
            }
            // Collapse every faded-out card in ONE pass once the fade finishes, again inside a FLIP.
            // Previously each card ran its own timer and removed itself from the grid independently,
            // so the survivors jumped position several times in a row — the "sloppy" shifting.
            clearTimeout(shop._collapseT);
            shop._collapseT = setTimeout(() => {
                const gone = [].slice.call(shop.querySelectorAll('.pcell.pc-hide'));
                if (!gone.length) return;
                flipGrid(shop, () => {
                    gone.forEach(c => { c.style.display = 'none'; c.classList.remove('pc-hide'); });
                });
            }, 640);
            shop.querySelector('.pgrid').style.flexDirection = '';   // grid; order via CSS below
            shop.querySelector('.pgrid').style.direction = tz === 'old' ? 'rtl' : 'ltr';
            const c = shop.querySelector('.pf-count'); if (c) c.textContent = shown + ' items';
            scheduleHeaderSample();   // different cells now sit under the header
            reflectSearchQuery();     // the query beside the magnifier follows every change, resets included
        }
        // re-entering the shop always starts CLEAN — clear the search + reset every filter/dropdown
        function resetShopFilters() {
            const shop = document.getElementById('shop');
            if (!shop) return;
            const s = shop.querySelector('[data-filter="search"]'); if (s) s.value = '';
            const d = shop.querySelector('[data-filter="designer"]'); if (d) d.value = '';
            const dz = shop.querySelector('[data-designer-open] .pf-dz'); if (dz) dz.textContent = 'All designers';
            const p = shop.querySelector('[data-filter="price"]'); if (p) p.value = '';
            const t = shop.querySelector('[data-filter="time"]'); if (t) t.value = 'new';
            shop.querySelectorAll('.pf-dd').forEach(dd => {
                const inp = dd.querySelector('[data-filter]'); if (!inp) return;
                const opts = DD_OPTS[inp.getAttribute('data-filter')]; if (!opts) return;
                const curOpt = opts.find(o => o.v === inp.value) || opts[0];
                const cur = dd.querySelector('.pf-dd-cur'); if (cur) cur.textContent = curOpt.l;
            });
            // restore every cell visible + clean (the cells-in cascade re-animates them on landing)
            clearTimeout(shop._collapseT);   // a pending collapse must not fire after a reset
            shop.querySelectorAll('.pcell').forEach(c => {
                clearTimeout(c._hideT);
                c.style.display = ''; c.style.transform = ''; c.style.transition = '';
                c.classList.remove('pc-hide', 'pc-reveal');
            });
            applyShopFilter();
        }
        document.querySelectorAll('#shop [data-filter]').forEach(s => s.addEventListener('change', applyShopFilter));
        // search fields filter live (input), and the button expands/collapses them
        // eased glide to the top of the lattice — decelerates and brakes into 0 (not a snap)
        let _searchScrollRAF = 0;
        function glideShopToTop() {
            if (!shopEl || shopEl.scrollTop <= 1) return;
            cancelAnimationFrame(_searchScrollRAF);
            const start = shopEl.scrollTop, t0 = performance.now(), dur = 820;
            (function step(now) {
                const p = Math.min(1, (now - t0) / dur);
                const e = 1 - Math.pow(1 - p, 4);          // quart ease-out: glides fast, brakes softly at the top
                shopEl.scrollTop = start * (1 - e);
                if (p < 1) _searchScrollRAF = requestAnimationFrame(step);
            })(t0);
        }
        // typing re-filters on a short debounce: mid-word keystrokes no longer restart the
        // fade on every character (the grid used to flicker as you typed), so the results
        // settle once you pause — one smooth in/out instead of a stutter per letter
        let _searchDebounce = 0;
        document.querySelectorAll('#shop [data-filter="search"]').forEach(s => s.addEventListener('input', () => {
            clearTimeout(_searchDebounce);
            _searchDebounce = setTimeout(() => {
                applyShopFilter();
                // starting a search brings the lattice back to the top so the filtered pieces
                // line up under the bar — smoothly, with a soft brake rather than a jump
                glideShopToTop();
            }, 180);
        }));
        // shared by the search button and the Enter key — both mean "I'm done, show me"
        function closeMenu(hd) {
            if (!hd || !hd.classList.contains('nav-open')) return;
            hd.classList.remove('nav-open');
            const b = hd.querySelector('[data-nav-toggle]');
            if (b) b.setAttribute('aria-expanded', 'false');
            if (window.innoLockScroll) window.innoLockScroll(false);
            innoMenuClear(hd);
        }
        document.querySelectorAll('.pf-search-input').forEach(inp => {
            inp.addEventListener('keydown', (e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                inp.blur();
                closeMenu(inp.closest('.page-header'));
            });
        });
        document.querySelectorAll('[data-search-toggle]').forEach(btn => {
            const wrap = btn.closest('.pf-search');
            const field = wrap.querySelector('.pf-search-input');
            /* On a desk the field itself is never shown: the magnifier IS the search, and it opens
               the glass (openSearch, below). The real input stays in the DOM as the filter's one
               source of truth — the snapshot, the reset and the live filter all read it — which is
               why it comes out of the tab order there instead of being deleted. On a phone it
               starts as the icon and opens inside the panel exactly as it always has. */
            const fitField = () => {
                if (deskLayout.matches) {
                    wrap.classList.remove('open');
                    field.tabIndex = -1;
                    field.setAttribute('aria-hidden', 'true');
                } else {
                    field.removeAttribute('tabindex');
                    field.removeAttribute('aria-hidden');
                }
            };
            fitField();
            if (deskLayout.addEventListener) deskLayout.addEventListener('change', fitField);
            btn.addEventListener('click', (e) => {
                e.preventDefault();
                const input = field;
                if (window.matchMedia('(max-width: 600px)').matches) {
                    // The field lives INSIDE the menu, so it has to stay open while you type —
                    // the first tap opens the bar and focuses it. The second tap means "show me",
                    // so it keeps the query and drops the panel instead of clearing it, which is
                    // the only way the filtered lattice underneath ever becomes visible.
                    if (wrap.classList.toggle('open')) { input.focus(); return; }
                    input.blur();
                    closeMenu(btn.closest('.page-header'));
                    return;
                }
                openSearch();
            });
        });

        /* ---- SEARCH AS A PLACE, NOT A FIELD (desktop) ----
           The magnifier opens a pane of glass over the shop. Everything underneath stays exactly
           where it is — blurred, not covered — and the live filter keeps running behind it, so
           as you type the pieces can be seen leaving and arriving through the blur. Enter clears
           the glass and the results are simply there.
           What you type is written into the real search field and announced with the same
           `input` event the field always sent, so the debounced filter, the snapshot that carries
           a search across page loads, and the reset on a fresh visit all work untouched: this is
           a new way of typing into the old search, not a second search. */
        let searchOv = null, searchReport = null;
        function realSearchField() { return document.querySelector('#shop [data-filter="search"]'); }
        function searchCount() {
            const f = shopFilterState();
            let n = 0;
            document.querySelectorAll('#shop .pcell').forEach(c => { if (c.querySelector('.p-t') && cellMatches(c, f)) n++; });
            return n;
        }
        // immediate, from the same predicate the grid uses — it does not wait for the debounce,
        // so the number moves with the typing while the pieces catch up behind the glass
        function paintSearchCount() {
            if (!searchOv) return;
            const n = searchCount();
            searchOv.querySelector('.isv-n').textContent = n === 1 ? '1 piece' : n + ' pieces';
        }
        function buildSearchOverlay() {
            const el = document.createElement('div');
            el.id = 'inno-search';
            el.setAttribute('role', 'dialog');
            el.setAttribute('aria-modal', 'true');
            el.setAttribute('aria-label', 'Search the archive');
            el.innerHTML =
                '<div class="isv-panel">' +
                    '<label class="isv-k" for="isv-q">Search</label>' +
                    '<input id="isv-q" class="isv-q hot" type="text" autocomplete="off" autocapitalize="off" autocorrect="off" spellcheck="false" enterkeyhint="search" placeholder="Designer or piece">' +
                    '<div class="isv-meta"><span class="isv-n" aria-live="polite"></span>' +
                        '<span class="isv-keys"><span>[ Enter ] Show</span><span>[ Esc ] Close</span></span></div>' +
                '</div>';
            document.body.appendChild(el);
            const q = el.querySelector('.isv-q');
            q.addEventListener('input', () => {
                const real = realSearchField();
                if (!real) return;
                real.value = q.value;
                real.dispatchEvent(new Event('input', { bubbles: true }));
                paintSearchCount();
            });
            q.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') { e.preventDefault(); closeSearch(); }
                else if (e.key === 'Tab') e.preventDefault();          // one field: focus stays in the dialog
            });
            // the glass itself (not the panel) closes it, keeping what was typed
            el.addEventListener('click', (e) => { if (e.target === el) closeSearch(); });
            return el;
        }
        function openSearch() {
            if (!deskLayout.matches) return;
            if (!searchOv) searchOv = buildSearchOverlay();
            if (document.querySelector('.pf-dd.open')) closeDropdowns(null);
            // the whole catalogue comes in NOW, so the very first keystroke already searches all of it
            if (window.innoFillCatalogue) window.innoFillCatalogue();
            const q = searchOv.querySelector('.isv-q');
            q.value = (realSearchField() || {}).value || '';
            paintSearchCount();
            document.body.classList.add('search-open');
            searchOv.classList.add('on');
            q.focus({ preventScroll: true });
            q.setSelectionRange(q.value.length, q.value.length);
        }
        /* ENTER IS THE CHECK. The glass is still up while this runs, so whatever it has to correct
           happens out of sight and the results come into focus already settled:
             1. the whole catalogue is in the lattice — nothing can be missing for not having
                been paged in yet, and every piece in the data is accounted for by its card;
             2. one authoritative pass of the real filter, the debounce abandoned;
             3. every card agrees with the query — anything still mid-fade is finished, and
                any card whose state disagrees with the predicate is put right and counted;
             4. the cards now on screen are told to load their photographs, and the field's
                scaffold behind them re-forms around where they now sit.
           The result is kept for inspection: window.innoSearchCheck(). */
        function settleSearchResults() {
            const shop = document.getElementById('shop');
            clearTimeout(_searchDebounce);
            if (cellCount < PRODUCTS.length) appendCells(PRODUCTS.length);
            applyShopFilter();
            const f = shopFilterState();
            const filtering = !!(f.dz || f.pz || f.sq);
            clearTimeout(shop._collapseT);                          // no collapse left to land later
            const cells = [].slice.call(shop.querySelectorAll('.pcell'));
            let shown = 0, corrected = 0;
            cells.forEach(cell => {
                if (!cell.querySelector('.p-t')) return;
                const ok = cellMatches(cell, f);
                clearTimeout(cell._hideT);
                if (ok) {
                    shown++;
                    if (cell.style.display === 'none') corrected++;
                    cell.classList.remove('pc-hide');
                    if (filtering) cell.classList.remove('pc-out');
                    cell.style.display = '';
                } else {
                    if (cell.style.display !== 'none' && !cell.classList.contains('pc-hide')) corrected++;
                    cell.classList.remove('pc-hide');
                    cell.style.display = 'none';
                }
            });
            const pids = new Set(cells.map(c => c.getAttribute('data-pid')));
            const missing = PRODUCTS.filter(p => p && p.pid != null && !pids.has(String(p.pid))).map(p => p.t);
            if (missing.length) console.warn('[innocences search] pieces with no card:', missing);
            /* To the top INSTANTLY, not by the glide the live filter uses. Both callers run this
               with the glass still up, so the jump is never seen — and a glide would still be
               moving the lattice while the glass cleared, when the whole point is that the
               results are sitting still by the time they come into focus. */
            if (shopEl) { cancelAnimationFrame(_searchScrollRAF); shopEl.scrollTop = 0; }
            revealInView();
            if (shopEl) shopEl.dispatchEvent(new Event('scroll'));   // inno-imgload sweeps on scroll
            syncShopTargets();
            const c = shop.querySelector('.pf-count'); if (c) c.textContent = shown + ' items';
            searchReport = { query: f.sq, shown: shown, corrected: corrected, cards: cells.length,
                             catalogue: PRODUCTS.length, missing: missing.length, at: new Date().toISOString() };
            return searchReport;
        }
        function closeSearch() {
            if (!searchOv || !searchOv.classList.contains('on')) return;
            settleSearchResults();
            searchOv.classList.remove('on');
            document.body.classList.remove('search-open');
            const btn = document.querySelector('#shop [data-search-toggle]');
            if (btn) btn.focus({ preventScroll: true });
        }
        /* The query stays beside the magnifier once the glass has gone, so a filtered shop always
           says it is filtered — and it is the way back in to change or clear it. Called from the
           end of every filter pass, so a reset or a restored snapshot updates it too. */
        function reflectSearchQuery() {
            const inp = realSearchField();
            const wrap = inp && inp.closest('.pf-search');
            if (!wrap) return;
            const v = (inp.value || '').trim();
            let tag = wrap.querySelector('.pf-q');
            if (!tag) {
                tag = document.createElement('span');
                tag.className = 'pf-q hot';
                tag.setAttribute('role', 'button');
                tag.tabIndex = 0;
                tag.addEventListener('click', (e) => { e.preventDefault(); openSearch(); });
                tag.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openSearch(); } });
                wrap.appendChild(tag);
            }
            tag.textContent = v;
            if (v) tag.setAttribute('aria-label', 'Search: ' + v + ' — change');
            else tag.removeAttribute('aria-label');
            wrap.classList.toggle('has-q', !!v);
        }
        /* ---- A RE-SORT, BEHIND THE GLASS (desktop, price) ----
           Filtering by price moves a lot at once: cards fade out, the survivors glide into the
           gaps, the lattice collapses and re-flows. In plain view that reads as the grid
           coming apart. So the glass comes up first, the sort happens behind it — still
           visible as motion, just not as mess — the result is settled and checked exactly as
           a search's is on Enter, and only then does the glass clear, onto cards that are
           already sitting still. The same glass as search, without the field. */
        const VEIL_IN = 240;     // the blur is well on its way before anything moves
        const VEIL_HOLD = 900;   // long enough for the fade, the collapse (640ms) and the glide
        let veilEl = null, veilBusy = false;
        function veilThrough(work) {
            if (veilBusy) { work(); return; }                 // a second choice mid-sort just applies
            veilBusy = true;
            if (!veilEl) {
                veilEl = document.createElement('div');
                veilEl.id = 'inno-veil';
                veilEl.setAttribute('aria-hidden', 'true');
                document.body.appendChild(veilEl);
            }
            document.body.classList.add('veil-open');
            void veilEl.offsetWidth;
            veilEl.classList.add('on');
            setTimeout(() => {
                work();
                setTimeout(() => {
                    settleSearchResults();                    // the same check Enter runs
                    veilEl.classList.remove('on');
                    document.body.classList.remove('veil-open');
                    veilBusy = false;
                }, VEIL_HOLD);
            }, VEIL_IN);
        }
        window.innoOpenSearch = openSearch;
        window.innoCloseSearch = closeSearch;
        window.innoSearchCheck = () => searchReport;
        applyShopFilter();

        // (search-suggestions dropdown removed — the search filters the lattice live instead
        //  of showing a separate preview box)

        // dots compose into the ACTUAL rendered lattice cells — measured from
        // the DOM so the scaffold and the cards can never drift apart
        function syncShopTargets() {
            if (!points) return;
            const n = points.geometry.attributes.aCard.count;
            const vw2 = Math.max(1, window.innerWidth), vh2 = Math.max(1, window.innerHeight);
            const rects = [...shopEl.querySelectorAll('.pcell')]
                .map(c => c.getBoundingClientRect())
                .filter(r => r.top < vh2 && r.bottom > 0)
                .slice(0, 12)
                .map(r => ({
                    x: (r.left / vw2 - 0.5) * visibleWidth,
                    y: (0.5 - r.bottom / vh2) * visibleHeight,
                    w: r.width / vw2 * visibleWidth,
                    h: r.height / vh2 * visibleHeight
                }));
            if (!rects.length) return;
            const fr2 = (x) => x - Math.floor(x);
            const arr = new Float32Array(n * 2);
            for (let k = 0, ci = 0; k < n; k++) {
                const r = rects[k % rects.length];
                const h1 = fr2(Math.sin(k * 12.9898) * 43758.5453);
                const h2 = fr2(Math.sin(k * 78.2330) * 12543.1230);
                const h3 = fr2(Math.sin(k * 37.7190) * 26711.7700);
                let tx, ty;
                if (h1 < 0.68) {
                    const p = h2 * 2 * (r.w + r.h);
                    if (p < r.w)                 { tx = r.x + p; ty = r.y; }
                    else if (p < r.w + r.h)      { tx = r.x + r.w; ty = r.y + (p - r.w); }
                    else if (p < 2.0 * r.w + r.h){ tx = r.x + (p - r.w - r.h); ty = r.y + r.h; }
                    else                         { tx = r.x; ty = r.y + (p - 2 * r.w - r.h); }
                } else {
                    tx = r.x + h3 * r.w;
                    ty = r.y + h2 * r.h;
                }
                arr[ci++] = tx;
                arr[ci++] = ty;
            }
            points.geometry.setAttribute('aCard', new THREE.BufferAttribute(arr, 2));
        }

        // per-page field "mood": the Index page runs a wilder, more reactive field
        // (noise ground + vortex cursor); everything else keeps the homescreen wave.
        // Captured lazily so it restores to whatever the settings established.
        let homeMood = null;
        // when non-null, overrides settings.field as the base field — the animate loop
        // eases the crossfade toward it (uModeB/uModeMix), so the mood fades in, never snaps
        let moodField = null;
        function restoreFieldMood() {
            if (!homeMood) return;
            const U = material.uniforms;
            moodField = null;                        // ease back to the homescreen wave
            U.uPointerMode.value = homeMood.pointer;
            U.uWaveAmt.value = homeMood.wave;
        }
        function applyFieldMood(dest) {
            const U = material.uniforms;
            if (!homeMood) homeMood = { pointer: U.uPointerMode.value, wave: U.uWaveAmt.value };
            // Every destination keeps the homescreen wave now, INDEX included. It used to get a
            // wilder field of its own (noise ground, vortex cursor), but INDEX is a log you read
            // from start to finish over the same mesh the welcome page shows — a busier field
            // behind long-form text is the one thing that makes long-form text harder to read.
            restoreFieldMood();
        }

        // The DOM hand-off. It used to live inline in the animation loop, gated on a WebGL uniform
        // crossing a threshold — so if the field stalled or the context never came up (low-end
        // phones, backgrounded tabs, a GPU hiccup) Shop/Visit/Index simply never appeared and the
        // buttons looked dead. It's now callable, and `enterPage` arms a watchdog that calls it
        // regardless, so navigation can never be held hostage by the field.
        let settleWatchdog = null;
        function settlePage() {
            clearTimeout(settleWatchdog); settleWatchdog = null;
            if (shopPhase !== 'entering' || !destOverlay) return;
            shopPhase = 'shop';
            destOverlay.classList.add('visible');
            document.body.classList.add('page-settled');
            if (destOverlay.id === 'shop') document.body.classList.add('header-mesh');
            // fade the text nav in fresh on every landing (headers are static,
            // so re-trigger the CSS animation by reflowing the .nav-in class)
            const ph = destOverlay.querySelector('.page-header');
            if (ph) { ph.classList.remove('nav-in'); void ph.offsetWidth; ph.classList.add('nav-in'); }
            // cascade the product cells in each time the shop lands
            if (destOverlay.id === 'shop') {
                destOverlay.classList.remove('cells-in'); void destOverlay.offsetWidth;
                destOverlay.classList.add('cells-in');
                // Re-sweep now the lattice actually HAS layout. The sweep that runs straight
                // after the cells are appended measures a hidden overlay, where every rect reads
                // as near and so every card reveals at once; without this correction the state
                // only settles on the first scroll, and nothing below the fold would fade.
                if (window.innoRevealInView) window.innoRevealInView();
                /* Get the REST of the catalogue into the DOM while nothing is happening, so a
                   search never has to build seventy cells mid-keystroke. That synchronous insert
                   is what made filtering feel choppy — the first match appeared instantly because
                   its card already existed, and the others waited on a build plus a scroll sweep.
                   Cells are cheap here: the photographs are data-inno-src and fetch nothing until
                   they come near the viewport. Idle time if the browser offers it, a plain timer
                   if not, and always after the landing cascade has had the screen to itself. */
                clearTimeout(window.__innoFillT);
                window.__innoFillT = setTimeout(function () {
                    const fill = function () { if (window.innoFillCatalogue) window.innoFillCatalogue(); };
                    if (window.requestIdleCallback) requestIdleCallback(fill, { timeout: 2000 });
                    else fill();
                }, 1200);
            }
            // the destination is live underneath — drop the cover
            if (window.innoLoader && window.innoLoader.hide) window.innoLoader.hide();
            // and the bed comes back up. The duck is for the TRANSITION — it was only ever
            // released by leaveShop(), so the bed stayed at zero for the whole visit to the
            // lattice. Since the player's only control on that screen is inside the hamburger
            // panel, pressing play there flipped the button to "pause" and produced silence.
            if (window.innoDuck) window.innoDuck(false);
        }

        /* ---------- the back button follows the pages ----------
           Shop, Visit and Index are overlays on ONE document, so moving between them never touched
           history, and the back button skipped past all of it to the last real page load. Shop →
           a piece → back to the shop → home by the wordmark, then back: it returned you to the
           PIECE, two screens ago, from a homescreen you'd just been looking at. Every page change
           now writes an entry, so back retraces what was actually on screen. */
        const HIST_DESTS = ['shop', 'archive', 'about'];
        // the address bar says what the visitor calls the page; `archive` is only its id in here
        const URL_NAME = { archive: 'visit' };
        function destFromParam(v) { return v === 'visit' ? 'archive' : (HIST_DESTS.indexOf(v) !== -1 ? v : null); }
        // set by a back/forward: the page being returned to comes back as it was left
        let histReturn = null;
        function recordNav(dest) {
            if (!window.history || !history.pushState) return;
            const st = history.state || {};
            if (st.inno === dest) return;   // history is already here — this change came FROM a back/forward
            // straight back to the screen we just came from: step back rather than stack a copy,
            // so home → shop → home doesn't leave the shop waiting behind you on the way out
            if (st.innoPushed && st.innoPrev === dest) { history.back(); return; }
            try {
                history.pushState({ inno: dest, innoPrev: st.inno || 'home', innoPushed: true }, '',
                    location.pathname + (dest === 'home' ? '' : '?open=' + (URL_NAME[dest] || dest)));
            } catch (err) {}
        }
        window.addEventListener('popstate', (e) => {
            const dest = (e.state && e.state.inno) || 'home';
            if (dest !== 'home' && HIST_DESTS.indexOf(dest) === -1) return;
            // anything layered over the page goes with it — back means "not this"
            const dm = document.getElementById('designer-modal');
            if (dm && dm.classList.contains('open')) closeDesigner();
            histReturn = dest === 'home' ? null : dest;
            if (dest === 'home') {
                pendingGoto = null;
                if (shopPhase !== 'home') leaveShop();
            } else if (shopPhase === 'home') {
                enterPage(dest);
            } else if (shopPhase === 'leaving') {
                pendingGoto = dest;
            } else if (!(destOverlay && destOverlay.id === dest)) {
                pendingGoto = dest;
                leaveShop();
            }
        });

        function enterPage(dest, e) {
            if (shopPhase === 'entering' || shopPhase === 'shop') return;
            const returning = histReturn === dest;
            histReturn = null;
            if (HIST_DESTS.indexOf(dest) !== -1) recordNav(dest);
            setInvertCenter(e);
            const page = PAGES[dest] || PAGES.shop;
            material.uniforms.uDest.value = page.dest;
            destOverlay = page.el;
            if (dest === 'shop') {
                /* Going BACK into the shop puts you where you were: same filters, same search, same
                   place in the lattice. Only choosing SHOP fresh lands on a clean, unfiltered top.
                   Within this document the lattice simply kept its state while it was hidden; a
                   document that has just loaded on a return gets it from the snapshot (below). */
                if (shopSnapOnLoad) { applyShopSnap(shopSnapOnLoad); shopSnapOnLoad = null; }
                else if (!returning) { resetShopFilters(); shopEl.scrollTop = 0; }
                syncShopTargets();
                document.body.classList.add('shop-page');
                refreshHeaderEls();
                setTimeout(scheduleHeaderSample, 120);
                setTimeout(scheduleHeaderSample, 520);
                setTimeout(scheduleHeaderSample, 980);
                if (window.innoDuck) window.innoDuck(true);    // quiet the bed under the transition
            }
            if (dest === 'about') page.el.scrollTop = 0;   // the log always opens at its head, where the cascade plays
            document.body.classList.toggle('about-page', dest === 'about');
            document.body.classList.toggle('archive-page', dest === 'archive');
            applyFieldMood(dest);
            shopPhase = 'entering';
            shopT = 0;
            document.body.classList.add('shop-mode');
            strengthTarget = 0;   // kill the pointer ripple now so the transition doesn't look mouse-driven
            // safety net: the field normally hands off in well under a second. If it hasn't by now,
            // land the page anyway rather than stranding the visitor on the homescreen.
            clearTimeout(settleWatchdog);
            // The SPA transition had no cover at all: tapping SHOP handed you the field morph
            // and whatever state the lattice happened to be in. Same readout as every other
            // navigation now, so moving between sections reads consistently.
            if (window.innoLoader && window.innoLoader.show) {
                const LBL = { shop: 'SHOP', archive: 'VISIT', about: 'INDEX', cart: 'CART' };
                // the lattice always opens sorted newest-first, so the cover says so
                window.innoLoader.show(LBL[dest] || String(dest).toUpperCase(), 0, dest === 'shop' ? 'New arrivals' : '');
            }
            settleWatchdog = setTimeout(settlePage, 1400);
        }
        function leaveShop(e) {
            if (shopPhase === 'home' || shopPhase === 'leaving') return;
            // a queued page→page pass records its destination when it enters; only a real return home records here
            if (!pendingGoto) recordNav('home');
            setInvertCenter(e);
            /* Put the overlay's own furniture away on the way out, whichever route got us here.
               An overlay that hides does NOT shut out its subtree: .page-ov drops to
               pointer-events:none, but a descendant setting `auto` beats an ancestor's `none`.
               Two things in there do exactly that — the menu panel, which is a full-viewport
               fixed sheet, and an armed card's quick actions — and either one left behind sits
               invisibly over the homescreen eating taps. */
            document.querySelectorAll('.page-header.nav-open').forEach(function (hd) {
                hd.classList.remove('nav-open');
                const b = hd.querySelector('[data-nav-toggle]');
                if (b) b.setAttribute('aria-expanded', 'false');
                innoMenuClear(hd);
            });
            if (window.innoLockScroll) window.innoLockScroll(false);
            disarmCard();
            shopPhase = 'leaving';
            strengthTarget = 0;    // freeze the pointer ripple for a clean invert-back
            window.__leaveT = 0;   // beat to hold the full inverted mesh before inverting back
            destOverlay.classList.remove('visible');   // page content fades out
            document.body.classList.remove('page-settled');
            document.body.classList.remove('shop-page');
            document.body.classList.remove('about-page');
            document.body.classList.remove('archive-page');
            restoreFieldMood();
            if (window.innoDuck) window.innoDuck(false);       // back to the homescreen bed
        }
        window.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return;
            /* A window layered over the page owns Escape — it closes that and nothing else. The cart
               window closes itself on a document listener, which runs BEFORE this one on the way
               up, so by the time it got here the layer was already gone and it left the page too:
               Escape on the bag over INDEX landed you on the homescreen.
               Capture phase, so this looks while the layer is still there. */
            if (document.body.classList.contains('cart-win-open')) return;
            // the search glass closes, keeping the query — it must never fall through to
            // leaving the shop, which is what Escape means when nothing is layered on top
            if (document.body.classList.contains('search-open')) { if (window.innoCloseSearch) window.innoCloseSearch(); return; }
            if (document.body.classList.contains('inno-game')) return;   // the game owns Escape while it is up
            if (document.body.classList.contains('veil-open')) return;   // a re-sort is under way; it clears itself
            if (document.querySelector('.pf-dd.open')) { closeDropdowns(null); return; }
            const dm = document.getElementById('designer-modal');
            if (dm && dm.classList.contains('open')) { closeDesigner(); return; }
            leaveShop();
        }, true);

        // menu order maps directly onto page destinations
        const DESTS = ['shop', 'archive', 'about', 'cart'];
        document.querySelectorAll('.menu').forEach(menu => {
            const items = [...menu.querySelectorAll('.menu-item')];
            items.forEach((a, i) => {
                a.addEventListener('click', (e) => {
                    e.preventDefault();
                    const dest = DESTS[i] || 'shop';
                    if (dest === 'cart') openCart(); else enterPage(dest, e);   // cart layers over
                });
            });
        });
        // the header logo (and any [data-back]) returns to the homescreen
        document.querySelectorAll('.page-ov [data-back]').forEach(b => b.addEventListener('click', (e) => { e.preventDefault(); leaveShop(); }));

        // in-page links to another page (header nav, cart's "Enter the shop" CTA):
        // reverse out of the current page, then compose into the requested one
        let pendingGoto = null;
        // cart's own [data-goto] (the empty-state CTA) is handled by the cart drawer
        // wiring below, so exclude #cart here to avoid double-handling
        document.querySelectorAll('.page-ov:not(#cart) [data-goto]').forEach(el => {
            el.addEventListener('click', (e) => {
                e.preventDefault();
                const target = el.getAttribute('data-goto');
                if (target === 'cart') { openCart(); return; }   // cart layers over — no page transition
                // clicking the nav item for the page you're already on: do nothing
                if (destOverlay && destOverlay.id === target) return;
                pendingGoto = target;
                leaveShop();
            });
        });

        // ---------- full-screen designer picker (shop + archive filters) ----------
        const dmItems = ['<button type="button" class="dm-item hot" style="animation-delay:460ms" data-open-delay="460" data-designer="">All designers</button>']
            .concat(DESIGNERS.map((d, i) => {
                // base 460ms so the list arrives AFTER the white→mesh reveal, then staggers
                const del = 460 + Math.min(i + 1, 18) * 26;
                const attr = esc(d).replace(/"/g, '&quot;');
                return `<button type="button" class="dm-item hot" style="animation-delay:${del}ms" data-open-delay="${del}" data-designer="${attr}">${esc(d)}</button>`;
            })).join('');
        const dModal = document.createElement('div');
        dModal.id = 'designer-modal';
        dModal.setAttribute('aria-hidden', 'true');
        dModal.innerHTML = '<div class="dm-top"><span class="dm-title">Filter — Designer</span>'
            + '<button type="button" class="dm-close hot" aria-label="Close">×</button></div>'
            + '<div class="dm-list">' + dmItems + '</div>';
        document.body.appendChild(dModal);
        let designerTarget = null;
        function openDesigner(container) {
            if (!container) return;
            designerTarget = container;
            const cur = (container.querySelector('[data-filter="designer"]') || {}).value || '';
            dModal.querySelectorAll('.dm-item').forEach(b => {
                b.classList.toggle('is-cur', (b.getAttribute('data-designer') || '') === cur);
                b.style.animationDelay = b.dataset.openDelay + 'ms';   // restore the descending in-stagger
            });
            dModal.classList.remove('closing'); clearTimeout(dModal._closeT);
            dModal.style.removeProperty('--close-delay');
            dModal.classList.add('open');
            document.body.classList.add('modal-open');
        }
        function closeDesigner() {
            // reverse choreography: the vendor names fade out bottom-to-top, THEN the veil
            const items = dModal.querySelectorAll('.dm-item');
            const n = items.length;
            for (let i = 0; i < n; i++) items[i].style.animationDelay = ((n - 1 - i) * 15) + 'ms';
            const stagger = (n - 1) * 15 + 80;
            dModal.style.setProperty('--close-delay', stagger + 'ms');   // veil fades after the items clear
            dModal.classList.remove('open');
            dModal.classList.add('closing');
            clearTimeout(dModal._closeT);
            dModal._closeT = setTimeout(() => {
                dModal.classList.remove('closing');
                for (let j = 0; j < items.length; j++) items[j].style.animationDelay = items[j].dataset.openDelay + 'ms';
                dModal.style.removeProperty('--close-delay');
            }, stagger + 380);
            document.body.classList.remove('modal-open');
        }
        document.querySelectorAll('[data-designer-open]').forEach(btn =>
            btn.addEventListener('click', (e) => { e.preventDefault(); openDesigner(btn.closest('.page-ov')); }));
        dModal.addEventListener('click', (e) => {
            if (e.target === dModal || e.target.closest('.dm-close')) { closeDesigner(); return; }
            const item = e.target.closest('.dm-item');
            if (!item || !designerTarget) return;
            const val = item.getAttribute('data-designer') || '';
            const input = designerTarget.querySelector('[data-filter="designer"]');
            const lbl = designerTarget.querySelector('[data-designer-open] .pf-dz');
            if (input) { input.value = val; input.dispatchEvent(new Event('change', { bubbles: true })); }
            if (lbl) lbl.textContent = val || 'All designers';
            closeDesigner();
        });

        // ---------- cart ----------
        // The bag is the draggable cart WINDOW (inno-cart-drawer), the same on every page. The
        // homepage's old full-screen #cart overlay is gone; without the window, the cart page.
        /* Let go of the field: no hover mood, no camera pull, no pointer ripple. Anything that
           layers over the page (the bag, above all) calls this so the mesh underneath holds still
           rather than carrying on with whatever gesture opened it. */
        function calmField() {
            hoverMenuIdx = -1;
            hoverItemEl = null;
            strengthTarget = 0;
            fieldSX = -1;
        }
        function openCart() {
            calmField();
            if (typeof window.innoOpenCart === 'function') { window.innoOpenCart(); return; }
            window.location.href = '/cart';
        }
        /* ---------- the shop, as it was left ----------
           Leaving this document with the shop on screen (almost always: tapping into a piece) writes
           down where you were. Coming back (the back button, or a piece's "Shop" link) puts it back.
           A back/forward that finds this page still in the browser's cache needs none of this; the
           snapshot is for a return that reloads the page, which in-app browsers often do. */
        const SHOP_SNAP = 'inno_shop_snap';
        function shopFilterVal(k) { const el = shopEl.querySelector('[data-filter="' + k + '"]'); return el ? el.value : ''; }
        function saveShopSnap() {
            if (shopPhase !== 'shop' || destOverlay !== shopEl) return;
            try {
                sessionStorage.setItem(SHOP_SNAP, JSON.stringify({
                    top: shopEl.scrollTop, search: shopFilterVal('search'), designer: shopFilterVal('designer'),
                    price: shopFilterVal('price'), time: shopFilterVal('time') || 'new', t: Date.now()
                }));
            } catch (err) {}
        }
        window.addEventListener('pagehide', saveShopSnap);
        // and at the tap itself, in case the way out never fires pagehide
        shopEl.addEventListener('click', (ev) => { if (ev.target.closest && ev.target.closest('a[href]')) saveShopSnap(); }, true);
        function applyShopSnap(snap) {
            const setVal = (k, v) => { const el = shopEl.querySelector('[data-filter="' + k + '"]'); if (el) el.value = v; };
            setVal('search', snap.search || '');
            setVal('designer', snap.designer || '');
            setVal('price', snap.price || '');
            setVal('time', snap.time || 'new');
            const dz = shopEl.querySelector('[data-designer-open] .pf-dz'); if (dz) dz.textContent = snap.designer || 'All designers';
            shopEl.querySelectorAll('.pf-dd').forEach(dd => {
                const inp = dd.querySelector('[data-filter]'); if (!inp) return;
                const opts = DD_OPTS[inp.getAttribute('data-filter')]; if (!opts) return;
                const curOpt = opts.find(o => o.v === inp.value) || opts[0];
                const cur = dd.querySelector('.pf-dd-cur'); if (cur) cur.textContent = curOpt.l;
            });
            // every card, so the place you were exists to scroll back to
            appendCells(PRODUCTS.length);
            applyShopFilter();
            // arrive already filtered: no fade-out-then-collapse playing on the way in
            clearTimeout(shopEl._collapseT);
            shopEl.querySelectorAll('.pcell').forEach(c => {
                c.style.transition = ''; c.style.transform = '';
                if (c.classList.contains('pc-hide')) { c.style.display = 'none'; c.classList.remove('pc-hide'); }
                c.classList.remove('pc-reveal');
            });
            shopEl.scrollTop = snap.top || 0;
        }
        // name the entry this document loaded into after the page it opens on, so the first page
        // change has something true to step back to (a reload keeps the old state — kept if it agrees)
        let shopSnapOnLoad = null;
        try {
            const q0 = new URLSearchParams(location.search);
            const first = q0.get('shop') === '1' ? 'shop' : (destFromParam(q0.get('open')) || 'home');
            if (!history.state || history.state.inno !== first) history.replaceState({ inno: first }, '');
            // a return: the back/forward buttons, or a piece's "Shop" link (/?shop=1)
            const nav = performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
            const back = (nav && nav.type === 'back_forward') || q0.get('shop') === '1';
            const raw = first === 'shop' && back ? sessionStorage.getItem(SHOP_SNAP) : null;
            const snap = raw ? JSON.parse(raw) : null;
            if (snap && Date.now() - snap.t < 30 * 60 * 1000) shopSnapOnLoad = snap;
        } catch (err) {}
        // arriving from a product page's "View bag" (/?cart=1) → open the cart drawer
        if (new URLSearchParams(location.search).get('cart') === '1') {
            let boiling = false;
            try { boiling = !!document.getElementById('inno-boot') && !sessionStorage.getItem('inno_booted'); } catch (e) {}
            // if the boot is playing this load, wait for it to finish; otherwise soon
            if (boiling) document.addEventListener('inno:reveal-ui', openCart, { once: true });
            else setTimeout(openCart, 400);
        }
        // arriving from a product page's "← Shop" (/?shop=1) → open the shop lattice
        if (new URLSearchParams(location.search).get('shop') === '1') {
            const openShop = () => { if (shopPhase === 'home') enterPage('shop'); };
            let boilingS = false;
            try { boilingS = !!document.getElementById('inno-boot') && !sessionStorage.getItem('inno_booted'); } catch (e) {}
            if (boilingS) document.addEventListener('inno:reveal-ui', () => setTimeout(openShop, 180), { once: true });
            else setTimeout(openShop, 260);
        }
        // generic /?open=<shop|visit|about> → open that page (PDP header nav links; `archive` is still
        // accepted for Visit, so links written before the rename keep working)
        const openParam = destFromParam(new URLSearchParams(location.search).get('open'));
        if (openParam) {
            const openIt = () => { if (shopPhase === 'home') enterPage(openParam); };
            let boilingO = false;
            try { boilingO = !!document.getElementById('inno-boot') && !sessionStorage.getItem('inno_booted'); } catch (e) {}
            if (boilingO) document.addEventListener('inno:reveal-ui', () => setTimeout(openIt, 180), { once: true });
            else setTimeout(openIt, 260);
        }

        // ---------- custom filter dropdowns (price / time) ----------
        // one shared menu in the ROOT stacking context (appended to body) so the
        // header hairline (a body pseudo above #shop's z-context) can't cut into it
        const ddMenu = document.createElement('div');
        ddMenu.id = 'pf-menu';
        ddMenu.setAttribute('role', 'listbox');
        document.body.appendChild(ddMenu);
        let ddActive = null;
        function closeDropdowns() {
            if (ddMenu.classList.contains('open')) {
                ddMenu.classList.remove('open');
                ddMenu.classList.add('closing');            // plays pop-out, then hides
                clearTimeout(ddMenu._closeT);
                ddMenu._closeT = setTimeout(() => ddMenu.classList.remove('closing'), 160);
            }
            if (ddActive) ddActive.classList.remove('open');
            ddActive = null;
        }
        function openDropdown(dd) {
            const opts = DD_OPTS[dd.getAttribute('data-dd')] || [];
            const cur = (dd.querySelector('[data-filter]') || {}).value || '';
            ddMenu.innerHTML = opts.map(o =>
                `<button type="button" class="pf-dd-opt hot${o.v === cur ? ' is-cur' : ''}" data-val="${o.v}">${o.l}</button>`).join('');
            const r = dd.querySelector('.pf-dd-btn').getBoundingClientRect();
            // the (vertical) menu drops straight down from the button
            ddMenu.style.top = (r.bottom + 6) + 'px';
            ddMenu.style.left = r.left + 'px';
            ddMenu.style.minWidth = Math.max(r.width, 150) + 'px';
            ddMenu.classList.remove('closing'); clearTimeout(ddMenu._closeT);
            ddMenu.classList.add('open');
            dd.classList.add('open');   // rotates the caret
            ddActive = dd;
        }
        document.addEventListener('click', (e) => {
            const toggle = e.target.closest('[data-dd-toggle]');
            if (toggle) {
                e.preventDefault();
                const dd = toggle.closest('.pf-dd');
                if (ddActive === dd) closeDropdowns(); else { closeDropdowns(); openDropdown(dd); }
                return;
            }
            const opt = e.target.closest('#pf-menu .pf-dd-opt');
            if (opt && ddActive) {
                e.preventDefault();
                const input = ddActive.querySelector('[data-filter]');
                const cur = ddActive.querySelector('.pf-dd-cur');
                if (input) {
                    const val = opt.getAttribute('data-val');
                    const apply = () => { input.value = val; input.dispatchEvent(new Event('change', { bubbles: true })); };
                    // price on a desk re-sorts behind the glass; anything else, or the same
                    // choice again, applies directly as it always has
                    if (deskLayout.matches && input.getAttribute('data-filter') === 'price' && input.value !== val) veilThrough(apply);
                    else apply();
                }
                if (cur) cur.textContent = opt.textContent;
                closeDropdowns();
                return;
            }
            closeDropdowns();   // click anywhere else closes the menu
        });
        window.addEventListener('resize', closeDropdowns);

        // ---------- custom cookies consent (bottom-left; inverts when accepted) ----------
        const cookieEl = document.getElementById('cookie-consent');
        if (cookieEl) {
            try { if (localStorage.getItem('inno_cookies') === 'yes') { cookieEl.classList.add('accepted'); cookieEl.setAttribute('aria-pressed', 'true'); } } catch (e) {}
            cookieEl.addEventListener('click', () => {
                const yes = cookieEl.classList.toggle('accepted');
                cookieEl.setAttribute('aria-pressed', yes ? 'true' : 'false');
                try { localStorage.setItem('inno_cookies', yes ? 'yes' : 'no'); } catch (e) {}
                // record the choice with Shopify's privacy API when it's available
                try {
                    if (window.Shopify && Shopify.customerPrivacy && Shopify.customerPrivacy.setTrackingConsent) {
                        Shopify.customerPrivacy.setTrackingConsent(yes, function () {});
                    }
                } catch (e) {}
            });
        }

        // ---------- ambient player ----------
        // Lives in inno-player.js now, loaded on EVERY page, so the drone survives navigation
        // (state travels in sessionStorage and each page rebuilds the same generative pad).
        // Keeping a second copy here would have created a rival AudioContext on the homepage.

        // ---------- apply the field's settings ----------
        material.uniforms.uMode.value = parseFloat(settings.field);
        material.uniforms.uPointerMode.value = parseFloat(settings.cursor);
        material.uniforms.uRadius.value = settings.radius;
        material.uniforms.uPower.value = settings.power / 100;
        material.uniforms.uWaveAmt.value = parseFloat(settings.wave);
        material.uniforms.uTransMode.value = parseFloat(settings.trans);

        // ---------- live clock ----------
        function tickClock() {
            const s = new Date().toLocaleTimeString('en-GB', { timeZone: 'America/Toronto', hour12: false });
            document.querySelectorAll('.clock').forEach(el => { el.textContent = s; });
        }
        tickClock();
        setInterval(tickClock, 1000);

        // ---------- boot sequence ----------
        // Timeline: particles power on radially (0–2.2s, shader uBoot) →
        // UI elements fade/unblur in staggered while scramble-decoding.
        (function boot() {
            const targets = [];
            const activeLayout = document.querySelector('.layout.active');
            if (activeLayout) targets.push(...activeLayout.children);
            targets.push(document.getElementById('player'));

            targets.forEach((el, i) => {
                el.style.transitionDelay = (0.5 + i * 0.22) + 's';
                // decode the text as its element arrives
                setTimeout(() => {
                    el.querySelectorAll('[data-orig]').forEach(scramble);
                }, 650 + i * 220);
            });

            // ---- BIOS boot handoff: the mesh loads in on inno:reveal-mesh (the
            // boot's loading bar), then the UI comes in on inno:reveal-ui (boot done).
            // If the boot isn't playing this load, reveal both immediately. ----
            /* THE ONE THING THE HOMESCREEN NEVER SAID. The field reacts to touch, leans with the
               phone and hides a game — and none of that announces itself, so a visitor who doesn't
               happen to drag a finger across it never learns any of it. One line, dead centre, for
               about a second as you land, then gone: long enough to read, too brief to become
               furniture. It reappears whenever you come back to the homescreen. */
            const touchHint = document.createElement('div');
            touchHint.id = 'touch-hint';
            touchHint.setAttribute('aria-hidden', 'true');
            // a mouse cannot touch anything. Same invitation, the verb this device has.
            const HINT_FIRST = isTouchDevice ? 'Touch the 3D mesh' : 'Move through mesh';
            touchHint.textContent = HINT_FIRST;
            document.body.appendChild(touchHint);
            const HINT_TILT = 'Tilt your device';

            /* ONE VOICE, ONE LINE AT A TIME.

               Three separate things want to speak here — the boot handing over, arriving back on
               the homescreen, and the motion prompt being answered — and each used to flash the
               line itself the moment it fired. On a real handset they land within a second of
               each other, so a new line cut the previous one off mid-sentence and the centre of
               the screen looked like it was arguing with itself.

               They queue now. A line is shown for its full read, allowed to finish fading, and
               only then does the next one begin, after a beat. Nothing interrupts anything, and
               the same line is never said twice over — asking for one already on screen or
               already waiting its turn does nothing at all. */
            const HINT_ON = 1150;    // long enough to read
            const HINT_FADE = 500;   // matches the CSS fade-out; the line is still legible through it
            const HINT_GAP = 420;    // the beat of clear screen between one line and the next
            const hintQueue = [];
            let hintShowing = null, hintT = 0, firstSaid = false, tiltDue = false;

            function sayHint(text) {
                const line = text || HINT_FIRST;
                if (hintShowing === line || hintQueue.indexOf(line) !== -1) return;
                hintQueue.push(line);
                pumpHint();
            }
            function pumpHint() {
                if (hintShowing || !hintQueue.length) return;
                // these belong to the homescreen: anything asked for elsewhere waits for the way back
                if (document.body.classList.contains('shop-mode') || document.body.classList.contains('pre-boot')) return;
                const line = hintQueue.shift();
                hintShowing = line;
                touchHint.textContent = line;
                void touchHint.offsetWidth;                 // restart the fade rather than extend it
                touchHint.classList.add('on');
                hintT = setTimeout(function () {
                    touchHint.classList.remove('on');
                    hintT = setTimeout(function () {
                        hintShowing = null;
                        /* THE ORDER THE VISITOR LEARNS IN. The invitation has to land before the
                           thing it leads to: a tap is what iOS needs before it will even offer the
                           motion prompt, so TILT YOUR DEVICE only makes sense once TOUCH THE 3D
                           MESH has been read. If the answer comes back first — a quick tap, a
                           remembered permission — it waits here rather than jumping the queue. */
                        if (line === HINT_FIRST) {
                            firstSaid = true;
                            if (tiltDue) { tiltDue = false; hintQueue.push(HINT_TILT); }
                        }
                        hintT = setTimeout(pumpHint, HINT_GAP);
                    }, HINT_FADE);
                }, HINT_ON);
            }
            function flashTouchHint() { sayHint(HINT_FIRST); }
            document.addEventListener('inno:home', function () { setTimeout(flashTouchHint, 420); });

            /* THE SECOND LINE, WHERE THE FIRST ONE WAS. Granted, and the same line in the same
               place says what the phone can now do — one gesture taught, then the next, rather
               than a window with a title bar explaining a sensor. Declined, it says nothing at
               all: "tilt your device" over a field that cannot answer is worse than silence. */
            document.addEventListener('inno:motion', function (ev) {
                if (!(ev.detail && ev.detail.granted)) return;
                if (firstSaid) sayHint(HINT_TILT);
                else tiltDue = true;                        // the invitation has not been read yet
            });

            let _biosActive = false;
            try { _biosActive = !!document.getElementById('inno-boot') && !sessionStorage.getItem('inno_booted'); } catch (e) {}
            if (_biosActive) {
                // the mesh loads in with a physics speed-impulse (__innoImpulse)
                document.addEventListener('inno:reveal-mesh', () => { window.__innoMeshGo = true; window.__innoImpulse = 1.0; }, { once: true });
                document.addEventListener('inno:reveal-ui', () => { document.body.classList.remove('pre-boot'); setTimeout(flashTouchHint, 900); }, { once: true });
                // failsafe: never leave the mesh/UI hidden longer than ~10s
                setTimeout(() => { window.__innoMeshGo = true; document.body.classList.remove('pre-boot'); }, 10000);
            } else {
                window.__innoMeshGo = true;
                window.__innoImpulse = 1.0;
                setTimeout(() => document.body.classList.remove('pre-boot'), 120);
                setTimeout(flashTouchHint, 1000);           // no intro this time: it lands with the chrome
            }
            // clear stagger delays afterwards so later interactions stay snappy
            setTimeout(() => {
                targets.forEach(el => { el.style.transitionDelay = ''; });
            }, 4000);
        })();

        // ---------- render loop ----------
        const prevMouse = new THREE.Vector2(0, 0);
        const velSmooth = new THREE.Vector2(0, 0);
        const velTarget = new THREE.Vector2(0, 0);

        function animate(time) {
            requestAnimationFrame(animate);
            /* WIDTH only. A phone's browser chrome collapses and expands as you scroll, animating
               innerHeight by 60-80px over a dozen frames — and this line called onResize on every
               one of them, each rebuilding the whole particle grid (see onResize). Height changes
               are handled there, cheaply and on a debounce. */
            if (window.innerWidth !== winW) onResize();
            // uTime is now an accumulated, warp-able clock (set below, after dtF) so
            // the mesh load-in can inject a physics speed-impulse that decays to 1x

            // anchor the field cursor effect through the LIVE camera, so the dot
            // reaction stays exactly under the cursor/menu item even while the
            // hover camera pans and zooms (was offset — too high on about/cart)
            if (fieldSX >= 0) screenToPlane(fieldSX, fieldSY, mouseTarget);
            mouseSmooth.lerp(mouseTarget, 0.08);
            material.uniforms.uMouse.value.copy(mouseSmooth);

            velTarget.subVectors(mouseSmooth, prevMouse).multiplyScalar(8);
            if (velTarget.length() > 15) velTarget.setLength(15);
            velSmooth.lerp(velTarget, 0.1);
            material.uniforms.uMouseVel.value.copy(velSmooth);
            prevMouse.copy(mouseSmooth);

            // the field lets go the moment the cart window goes up, rather than waiting for
            // another pointer event to come along and tell it
            if (fieldInert()) strengthTarget = 0;

            material.uniforms.uMouseStrength.value +=
                (strengthTarget - material.uniforms.uMouseStrength.value) * 0.05;

            // menu-hover field crossfade: each menu item summons its own field
            // mode; leaving eases back to the saved base field
            const tSecF = time * 0.001;
            const dtF = Math.min(0.05, Math.max(0, tSecF - lastAnimT));
            lastAnimT = tSecF;

            // ---- physics load-in impulse ----
            // on mesh reveal the whole field gets one speed boost that decays back
            // to 1x (real physics). It warps the wave CLOCK and the particle assemble
            // together off the same clock, so wave + assemble read as one motion.
            window.__innoImpulse = (window.__innoImpulse || 0) * Math.pow(0.5, dtF / 0.5);
            if (window.__innoImpulse < 0.002) window.__innoImpulse = 0;
            const timeRate = 1 + window.__innoImpulse * 5.0;      // up to ~6x at the kick
            // reduced-motion → advance the wave clock at ~1/3 speed: a slow, calm drift
            const motionScale = INNO_MOTION_CALM ? 0.34 : 1;
            window.__innoWarp = (window.__innoWarp || 0) + dtF * timeRate * motionScale;
            material.uniforms.uTime.value = window.__innoWarp;
            // page mood (e.g. Index → noise) overrides the base field; the crossfade below eases to it
            const baseField = (moodField != null) ? moodField : parseFloat(settings.field);
            // each menu item summons its own field pattern on hover
            // shop → wave, archive → ripple, about → dome, cart → weave (ambient)
            const wantField = (settings.menuFx === 'field' && hoverMenuIdx >= 0)
                ? [1, 5, 10, 8][hoverMenuIdx % 4]
                : baseField;
            if (fieldA < 0) { fieldA = baseField; fieldB = baseField; }
            if (wantField !== fieldB) {
                fieldA = fieldMix > 0.5 ? fieldB : fieldA;
                fieldB = wantField;
                fieldMix = 0;
            }
            fieldMix = Math.min(1, fieldMix + dtF * 1.8);
            const fmEase = fieldMix * fieldMix * (3 - 2 * fieldMix);
            material.uniforms.uMode.value = fieldA;
            material.uniforms.uModeB.value = fieldB;
            material.uniforms.uModeMix.value = fmEase;

            // nav-hover inversion hint strength (center is set below, after the
            // camera is positioned, so it accounts for the hover perspective move)
            // no orange fill on menu hover — the menu hover is the camera
            // animation (progressive zoom + sway) only. the sole orange left is
            // the faint neutral tint that follows the cursor (vGlow).
            material.uniforms.uHint.value = 0;
            hintSnap = false;

            // ---- shop transition driver ----
            if (shopPhase === 'entering') shopT += dtF;
            // RETURN TO HOME: while leaving, hold the inversion at 1 for a beat so
            // the full white-on-black mesh reveals across the whole viewport (the
            // page content having faded out); then release it so the field inverts
            // back to the normal black ground with bone dots.
            if (shopPhase === 'leaving') window.__leaveT = (window.__leaveT || 0) + dtF;
            // hold only on a true return to the homescreen — a queued pendingGoto
            // means this is a page→page pass-through, which should snap through fast
            /* INDEX and VISIT are QUIET destinations: the field stays exactly as the welcome page
               has it — void ground, bone dots, the homescreen wave — with the page laid over the
               top. No sweep to bone, and no dots pulled into a layout, because these pages have
               nothing to compose them into; the field is their backdrop, not their content. */
            const quietDest = !!(destOverlay && (destOverlay.id === 'about' || destOverlay.id === 'archive'));
            const invHold = shopPhase === 'leaving' && !pendingGoto && !quietDest && (window.__leaveT || 0) < 0.75;
            const invTarget = quietDest ? 0 : ((shopPhase === 'entering' || shopPhase === 'shop' || invHold) ? 1 : 0);
            material.uniforms.uInvert.value +=
                (invTarget - material.uniforms.uInvert.value) * Math.min(1, dtF * 3.5);
            // PAGE MESH on: don't compose the dots into the page — leave the
            // field flowing as the live wave so it shows behind the header band
            const composePages = settings.pageMesh !== 'on';
            // run the dissolve only while ENTERING; once the page has settled,
            // drop uCardMix back to 0 so the field flows again as a calm ambient
            // wave that can sit semi-transparent behind the shop header (below).
            const cardTarget = (composePages && !quietDest && shopPhase === 'entering' && shopT > 0.45) ? 1 : 0;
            material.uniforms.uCardMix.value +=
                (cardTarget - material.uniforms.uCardMix.value) * Math.min(1, dtF * 2.2);
            // semi-transparent inverted mesh behind the shop header (settled state)
            document.body.classList.toggle('header-mesh',
                shopPhase === 'shop' && destOverlay && destOverlay.id === 'shop');
            // settle off the compose normally, or off the invert when the field
            // stays ambient (mesh mode), so the DOM still hands off on time
            // a quiet destination has neither an inversion nor a composition to wait for, so it
            // settles on time — otherwise it would only ever arrive via the 1.4s watchdog
            const settleReady = quietDest
                ? shopT > 0.5
                : composePages
                    ? material.uniforms.uCardMix.value > 0.85
                    : (material.uniforms.uInvert.value > 0.9 && shopT > 0.7);
            if (shopPhase === 'entering' && settleReady) settlePage();
            if (shopPhase === 'leaving' && material.uniforms.uInvert.value < 0.03) {
                shopPhase = 'home';
                document.dispatchEvent(new Event('inno:home'));   // back on the home screen (the motion note waits for this)
                document.body.classList.remove('shop-mode');
                if (pendingGoto) { const g = pendingGoto; pendingGoto = null; enterPage(g); }
            }
            // ground follows the inversion, in the chosen style
            const iv = material.uniforms.uInvert.value;
            material.uniforms.uInvertMode.value =
                settings.invertFx === 'sweep' ? 1 : settings.invertFx === 'fade' ? 0 : 2;
            if (iv < 0.002) {
                document.body.style.background = '#0a0a0a';
            } else if (iv > 0.998) {
                document.body.style.background = '#E8E4DC';
            } else if (settings.invertFx === 'sweep') {
                const p = iv * 112 - 6;
                document.body.style.background =
                    `linear-gradient(90deg, rgb(232,228,220) ${p}%, rgb(10,10,10) ${p + 6}%)`;
            } else if (settings.invertFx === 'ripple') {
                const rr = iv * Math.hypot(window.innerWidth, window.innerHeight);
                document.body.style.background =
                    `radial-gradient(circle at ${invertCX}px ${invertCY}px, rgb(232,228,220) ${Math.max(0, rr - 150)}px, rgb(10,10,10) ${rr}px)`;
            } else {
                const ch = (a, b) => Math.round(a + (b - a) * iv);
                document.body.style.background = `rgb(${ch(10, 232)},${ch(10, 228)},${ch(10, 220)})`;
            }

            // intro assembly is gated by the boot handoff: the dots converge into
            // the grid over ~3s once the mesh is signalled to load in (the boot's
            // loading bar), so the "load-in sequence" starts exactly then.
            if (window.__innoMeshGo) {
                // driven off the WARPED clock, so the particles rush in on the same
                // impulse as the wave (fast, then decelerating) — one unified motion
                if (window.__innoAssembleT0 == null) window.__innoAssembleT0 = window.__innoWarp || 0;
                const at = (window.__innoWarp || 0) - window.__innoAssembleT0;
                material.uniforms.uBoot.value = Math.min(1, at / 2.4);
                material.uniforms.uAssemble.value = Math.min(1, at / 3.4);
            } else {
                material.uniforms.uBoot.value = 0;
                material.uniforms.uAssemble.value = 0;
            }

            /* NO LAG. The crosshair used to ease toward the pointer, and on anything quicker
               than a drift it sat visibly behind the hand — a reticle that cannot keep up stops
               reading as the cursor and starts reading as something broken chasing it. */
            cursorX = cursorTX;
            cursorY = cursorTY;
            cursorEl.style.left = cursorX + 'px';
            cursorEl.style.top = cursorY + 'px';

            // menu hover camera: a progressive zoom-out — each item down the
            // list pulls back further — plus a tiny continued pan-out over a few
            // seconds and the smallest sway. Eases in/out between items.
            // gentler, more even progression — cart's pull is no longer drastic,
            // so the swirl/whirlpool patterns read cleanly instead of shrinking away
            const PULL_STEPS = [12, 20, 28, 36];   // shop → cart, descending = more zoom-out
            const navHover = hoverMenuIdx >= 0 && shopPhase === 'home';
            // pan-out: on each hover it keeps drifting out a touch over ~2.5s
            let panOut = 0;
            if (navHover) {
                const held = (performance.now() - hoverStartMs) / 1000;
                panOut = (1 - Math.exp(-held / 1.1)) * 10;
            }
            const pullTarget = navHover ? PULL_STEPS[hoverMenuIdx % 4] + panOut : 0;
            menuPull += (pullTarget - menuPull) * Math.min(1, dtF * 2.6);
            // sway strength eases in while hovering, out on leave
            menuSway += ((navHover ? 1 : 0) - menuSway) * Math.min(1, dtF * 1.8);
            const swayX = Math.sin(time * 0.00085) * 3.2 * menuSway;
            const swayY = Math.cos(time * 0.00120) * 2.2 * menuSway;

            // pan the field toward the hovered item's own screen position — so
            // about/cart (lower-right) pull the perspective down-right, loading
            // the view from where the item actually sits
            let panItemX = 0, panItemY = 0;
            if (navHover && hoverItemEl) {
                const r = hoverItemEl.getBoundingClientRect();
                const inx = ((r.left + r.width / 2) / window.innerWidth) * 2 - 1;
                const iny = -(((r.top + r.height / 2) / window.innerHeight) * 2 - 1);
                panItemX = inx * 16;
                panItemY = iny * 11;
            }
            menuPanX += (panItemX - menuPanX) * Math.min(1, dtF * 2.4);
            menuPanY += (panItemY - menuPanY) * Math.min(1, dtF * 2.4);

            const unlock = 1 - material.uniforms.uCardMix.value;
            /* Idle drift calms as the sway takes over — and as a LEAN takes over, which is the
               other half of why the two sides did not match. The drift is a ±10-unit sine
               running on its own clock: lean left while it happens to be at −10 and the camera
               reaches further than the same lean to the right a few seconds later. A deliberate
               lean now owns the camera and the wandering gets out of its way. */
            springTilt(dtF);   // momentum-carrying ease — see the note at its definition
            const leanMag = Math.min(1, Math.abs(tiltNdc.x));   // the camera answers to roll alone
            const idle = (1 - menuSway * 0.65) * (1 - leanMag);   // at a full lean, none at all
            updateTiltText();   // same eased value, opposite sign — see the note at its definition
            // the pointer keeps its gentle slide — that one is a parallax cue, not a viewpoint.
            // the gyro ORBITS instead (see ORBIT_YAW): a lean is a change of vantage.
            const px = pointerNdc.x;
            const py = pointerNdc.y;
            // east-west, and the height rides on the same number — one sweep, one input
            const yaw = tiltNdc.x * ORBIT_YAW, rise = yaw * ORBIT_RISE;
            const r = CAM_Z + menuPull * unlock - ORBIT_PULL * leanMag * unlock;   // lean in as you come round
            const cr = Math.cos(rise);
            const orbX = Math.sin(yaw) * cr * r, orbY = Math.sin(rise) * r, orbZ = Math.cos(yaw) * cr * r;
            camera.position.x = (Math.sin(time * 0.0005) * 10 * idle + px * 13 + swayX + menuPanX + orbX) * unlock;
            camera.position.y = (Math.cos(time * 0.0005) * 5 * idle + py * 9 + swayY + menuPanY + orbY) * unlock;
            // the arc collapses back to straight-on as a card takes the screen (unlock → 0)
            camera.position.z = r + (orbZ - r) * unlock;
            camera.lookAt(0, 0, 0);
            camera.updateMatrixWorld();

            // hint center + size: the hovered item's screen rect as NDC. cart and
            // about get pushed lower + right (their pulled-back camera reads better
            // with the orange offset toward the corner). snaps on item change so
            // the orange fades in AT the new item rather than sliding across.
            const HINT_OFFSETS = [[0, 0], [0, 0], [0.07, -0.11], [0.10, -0.16]];  // shop, archive, about, cart
            if (hoverItemEl) {
                const r = hoverItemEl.getBoundingClientRect();
                const off = HINT_OFFSETS[hoverMenuIdx % 4] || [0, 0];
                hintCenterTarget.set(
                    ((r.left + r.width / 2) / window.innerWidth) * 2 - 1 + off[0],
                    -(((r.top + r.height / 2) / window.innerHeight) * 2 - 1) + off[1]
                );
                hintSizeTarget.set(r.width / window.innerWidth, r.height / window.innerHeight);
            }
            const hk = hintSnap ? 1 : Math.min(1, dtF * 8);
            hintCenterSmooth.lerp(hintCenterTarget, hk);
            hintSizeSmooth.lerp(hintSizeTarget, hk);
            material.uniforms.uHintCenter.value.copy(hintCenterSmooth);
            material.uniforms.uHintSize.value.copy(hintSizeSmooth);

            const holdForScroll = coarsePointer && shopPhase === 'shop' && destOverlay === shopEl && performance.now() < shopScrollQuietAt;
            if (!holdForScroll) renderer.render(scene, camera);
            // the boot's loading bar waits on the field's first drawn frame (inno-boot.js)
            if (!window.__innoFieldFrame) { window.__innoFieldFrame = true; document.dispatchEvent(new Event('inno:field-frame')); }
        }

        // screen px → world point on the z=0 plane, via the current camera
        const _ndc = new THREE.Vector3();
        function screenToPlane(cx, cy, out) {
            _ndc.set((cx / window.innerWidth) * 2 - 1, -((cy / window.innerHeight) * 2 - 1), 0.5);
            _ndc.unproject(camera);
            _ndc.sub(camera.position);
            const t = -camera.position.z / _ndc.z;
            out.set(camera.position.x + _ndc.x * t, camera.position.y + _ndc.y * t);
            return out;
        }
        // The page can load while its container is still 0-sized (embedded
        // previews lay out after the iframe loads) and no resize event fires
        // afterward — so animate() also re-checks the window size every frame.
        let winW = -1, winH = -1;
        let lastAnimT = 0;
        let fieldA = -1, fieldB = -1, fieldMix = 1;   // field crossfade state
        /* Resizing the canvas is cheap. REBUILDING THE GRID is not: it allocates six fresh
           Float32Arrays (~440KB), walks 8,500 points, runs the target layouts (~100,000 sines) and
           re-uploads the lot to the GPU — 10-25ms on a mid phone. It used to run on every resize,
           and a phone fires those continuously while its URL bar slides away, so a single scroll
           gesture could pay for it a dozen times.
           The field is overscanned well past the viewport, so a grid built for a slightly different
           shape is indistinguishable. It is rebuilt only when the aspect ratio really moves (a
           rotation, a desktop window drag), and then on a debounce. */
        let gridAspect = 0, regridT = 0;
        function onResize(force) {
            winW = window.innerWidth;
            winH = window.innerHeight;
            // measured off the CONTAINER, which spans the tall viewport — window.innerHeight is
            // whatever the phone's chrome leaves behind at this instant, and drawing to that left
            // a strip of bare page at the bottom of the screen
            const w = Math.max(1, fieldBox.clientWidth || winW), h = Math.max(1, fieldBox.clientHeight || winH);
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
            orbitLimits();          // the swing the new shape of screen can afford
            renderer.setSize(w, h, false);
            const shape = w / h;
            if (force || !gridAspect) { gridAspect = shape; buildGrid(); return; }
            if (Math.abs(shape / gridAspect - 1) < 0.08) return;   // the URL bar, not a new shape
            clearTimeout(regridT);
            regridT = setTimeout(function () {
                gridAspect = window.innerWidth / Math.max(1, window.innerHeight);
                buildGrid();
            }, 220);
        }
        onResize(true);
        window.addEventListener('resize', function () { onResize(false); });
        // a phone's chrome sliding away resizes the VISUAL viewport without always firing `resize`
        if (window.visualViewport) window.visualViewport.addEventListener('resize', function () { onResize(false); });
        window.addEventListener('orientationchange', function () { setTimeout(function () { onResize(true); }, 260); });

        animate(0);

        /* Embedded preview panes suspend requestAnimationFrame, which leaves the field frozen on
           its first frame and nothing about its motion checkable there. This drives it by hand:
           innoFieldStep(16, 40) runs 40 frames of 16ms. Pair it with innoGyroTest(1, 0) to hold a
           full lean and see where the camera actually ends up. */
        window.innoFieldStep = function (ms, n, onFrame) {
            var step = ms || 16, frames = n || 1;
            // its own clock, carried between calls: restarting it from performance.now() every
            // time meant two calls in quick succession looked like no time had passed at all,
            // and anything eased simply did not advance — which is exactly what you are trying
            // to measure when you step one frame at a time
            if (!window.innoFieldStep._t) window.innoFieldStep._t = performance.now();
            for (var i = 0; i < frames; i++) {
                window.innoFieldStep._t += step;
                animate(window.innoFieldStep._t);
                if (onFrame) onFrame(camera.position.x, camera.position.y, camera.position.z);
            }
            return { frames: frames, camera: camera.position.toArray().map(function (v) { return +v.toFixed(1); }) };
        };
    